// @ts-nocheck
// Equine Performance Labs API Service - HIPAA/FDA Compliant
// Centralized API calls with authentication and error handling
// NOTE: ts-nocheck is used during gradual TS migration. Core utilities are typed;
// individual API method parameters will be typed incrementally.

/* eslint-disable @typescript-eslint/no-explicit-any */

import { logger } from '../utils/logger';

type AuthHeaders = Record<string, string>;

interface RequestOptions extends RequestInit {
  url?: string;
  headers?: Record<string, string>;
}

// =====================================================
// PERSPECTIVE HELPER - For Admin "View as Client" Feature
// =====================================================

/**
 * Get the effective client ID when an admin is viewing as a client.
 * Returns the target user ID from perspective context, or null if not in perspective mode.
 * This allows API calls to automatically include the clientId parameter.
 */
const getPerspectiveClientId = (): string | null => {
  try {
    const storedPerspective = sessionStorage.getItem('barebiologics_perspective');
    if (storedPerspective) {
      const parsed = JSON.parse(storedPerspective);
      // Only return the ID if we're in a CLIENT perspective
      if (parsed.currentPerspective === 'CLIENT' && parsed.targetUserId) {
        return parsed.targetUserId;
      }
    }
  } catch (e) {
    logger.error('Error reading perspective context:', e);
  }
  return null;
};

/**
 * Check if the current session is in "view as client" mode
 */
const isInClientPerspective = (): boolean => {
  return getPerspectiveClientId() !== null;
};

// Export perspective helpers for use in components
export { getPerspectiveClientId, isInClientPerspective };

// =====================================================
// ADMIN PARENT-COMPANY PREVIEW HELPERS
// =====================================================
//
// When an INTERNAL admin clicks "View As" on a parent company row,
// we stash the chosen parent_company id (+ display name) in
// sessionStorage and drop them into the regular /client/* portal.
// The portal pages render exactly as a parent admin sees them; the
// only difference is that API calls auto-inject
// ?adminPreviewParentCompanyId= so the backend scopes orders / etc.
// to that parent's children. The header is set only when the caller
// is INTERNAL -- a real PARENT_ADMIN ignores it (backend uses their
// own parent_company regardless).

const ADMIN_PARENT_PREVIEW_KEY = 'barebiologics_adminPreviewParent';

interface AdminParentPreview {
  parentCompanyId: string;
  parentCompanyName: string;
  organization?: string;
}

const getAdminParentPreview = (): AdminParentPreview | null => {
  try {
    const raw = sessionStorage.getItem(ADMIN_PARENT_PREVIEW_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (parsed?.parentCompanyId) return parsed;
  } catch {
    /* corrupt */
  }
  return null;
};

const setAdminParentPreview = (preview: AdminParentPreview): void => {
  sessionStorage.setItem(ADMIN_PARENT_PREVIEW_KEY, JSON.stringify(preview));
};

const clearAdminParentPreview = (): void => {
  sessionStorage.removeItem(ADMIN_PARENT_PREVIEW_KEY);
};

const getAdminParentPreviewId = (): string | null =>
  getAdminParentPreview()?.parentCompanyId || null;

export {
  getAdminParentPreview,
  setAdminParentPreview,
  clearAdminParentPreview,
  getAdminParentPreviewId,
};

// NOTE: API_BASE_URL and getAuthHeaders are exported at the bottom of the constants section

// Use REACT_APP_API_URL if available, otherwise detect environment
const getApiBaseUrl = (): string => {
  // Check for explicit environment variable first
  if (process.env.REACT_APP_API_URL) {
    logger.log('Using API URL from env:', process.env.REACT_APP_API_URL);
    return process.env.REACT_APP_API_URL;
  }
  
  // Auto-detect: if running on localhost, use local backend
  if (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1') {
    logger.log('Local development detected - using localhost:8080');
    return 'http://localhost:8080/api';
  }
  
  // Production fallback
  logger.log('Using production API');
  return 'https://barebiologics-api-bdgjaefwc4h7d9gu.eastus2-01.azurewebsites.net/api';
};

const API_BASE_URL: string = getApiBaseUrl();

// Base for public share links shown to humans. Points at the branded app origin
// (e.g. https://unisynhub.com/UnisynHub/#) — NOT the raw API host — so copied /
// emailed resource links don't expose the App Service hostname. The hash route
// (ResourceShareRedirect) then forwards the browser to the API's file-stream
// endpoint, which still streams the file and stamps open tracking.
const getShareLinkBase = (): string => {
  const base = (process.env.PUBLIC_URL || '').replace(/\/$/, '');
  return `${window.location.origin}${base}/#`;
};

// Public tracked-share URL for a stored token — the exact format minted by
// createTrackedShareLink, reused to surface existing links (e.g. in analytics).
const buildTrackedShareUrl = (token: string): string =>
  `${getShareLinkBase()}/r/${encodeURIComponent(token)}`;

// Public, no-login URL for a lead-capture form (hash route, matches App.jsx).
export const buildPublicFormUrl = (slug: string): string =>
  `${getShareLinkBase()}/f/${encodeURIComponent(slug)}`;

// Active donor unlock context — set by DonorDetailPage when a released donor is
// unlocked for editing. Stored at module scope (not localStorage) so the token
// lives only for the current tab/session.
let activeDonorUnlock: { donorId: string; token: string } | null = null;

export const setActiveDonorUnlock = (donorId: string | null, token: string | null): void => {
  activeDonorUnlock = donorId && token ? { donorId, token } : null;
};

export const getActiveDonorUnlock = (): { donorId: string; token: string } | null => activeDonorUnlock;

// Authentication helper
const getAuthHeaders = (): AuthHeaders => {
  const token = localStorage.getItem('authToken');
  const headers: AuthHeaders = {
    'Content-Type': 'application/json',
    'Authorization': token ? `Bearer ${token}` : '',
    'X-Client-Version': '2.0.0',
    'X-HIPAA-Compliant': 'true'
  };
  if (activeDonorUnlock) {
    headers['X-Donor-Unlock-Token'] = activeDonorUnlock.token;
  }
  return headers;
};

// Export API_BASE_URL and getAuthHeaders for use across the frontend
export { API_BASE_URL, getAuthHeaders };

// Token refresh functionality
const refreshToken = async (): Promise<string> => {
  try {
    const refreshTokenValue = localStorage.getItem('refreshToken');
    if (!refreshTokenValue) {
      throw new Error('No refresh token available');
    }

    const response = await fetch(`${API_BASE_URL}/auth/refresh`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Client-Version': '2.0.0',
        'X-HIPAA-Compliant': 'true'
      },
      credentials: 'include',
      body: JSON.stringify({ refreshToken: refreshTokenValue })
    });

    if (!response.ok) {
      throw new Error('Token refresh failed');
    }

    const data = await response.json();
    if (data.success && data.accessToken) {
      localStorage.setItem('authToken', data.accessToken);
      logger.log('Token refreshed successfully');
      return data.accessToken;
    } else {
      throw new Error('Invalid refresh response');
    }
  } catch (error) {
    logger.error('Token refresh failed:', error);
    // Clear invalid tokens
    localStorage.removeItem('authToken');
    localStorage.removeItem('refreshToken');
    // Don't redirect immediately - let the component handle the auth failure
    throw error;
  }
};

// Error handling wrapper
// Note: 401 token refresh is handled by the fetch interceptor in setupAPIInterceptors.
// This function no longer does its own refresh to avoid duplicate/competing refresh attempts.
const handleApiResponse = async (response: Response, _originalRequest: RequestOptions | null = null): Promise<any> => {
  if (!response.ok) {
    const error = await response.json().catch(() => ({ message: 'Network error' }));
    logger.error('API Error Details:', error);
    if (error.debug) {
      logger.error('Debug Info:', error.debug);
      logger.error('Requested ID:', error.debug.requestedId);
      logger.error('Available IDs:', error.debug.availableIds);
    }
    // Prioritize message over error field (error.error is often just "Error" string)
    const errorMessage = error.message || error.error || `HTTP ${response.status}`;
    throw new Error(errorMessage);
  }
  return response.json();
};

// Helper function for API calls with automatic token refresh
const apiCall = async (method: string, url: string, options: RequestOptions = {}): Promise<any> => {
  const requestOptions = {
    method: method,
    ...options,
    headers: {
      ...getAuthHeaders(),
      ...options.headers
    }
  };

  const response = await fetch(url, requestOptions);
  return await handleApiResponse(response, { url, ...requestOptions });
};

export const authAPI = {
  // Authentication endpoints
  login: async (credentials) => {
    const response = await fetch(`${API_BASE_URL}/auth/login`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(credentials)
    });
    return handleApiResponse(response);
  },

  logout: async () => {
    const response = await fetch(`${API_BASE_URL}/auth/logout`, {
      method: 'POST',
      headers: getAuthHeaders()
    });
    return handleApiResponse(response);
  },

  refreshToken: async () => {
    const refreshToken = localStorage.getItem('refreshToken');
    const response = await fetch(`${API_BASE_URL}/auth/refresh`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ refreshToken })
    });
    return handleApiResponse(response);
  },

  sendVerificationCode: async (email) => {
    const response = await fetch(`${API_BASE_URL}/auth/send-verification-code`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ email })
    });
    return handleApiResponse(response);
  },

  verifyCode: async (email, verificationCode) => {
    const response = await fetch(`${API_BASE_URL}/auth/verify-code`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ email, verificationCode })
    });
    return handleApiResponse(response);
  },

  signup: async (signupData) => {
    const response = await fetch(`${API_BASE_URL}/auth/signup`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(signupData)
    });
    return handleApiResponse(response);
  },

  register: async (userData) => {
    const response = await fetch(`${API_BASE_URL}/auth/register`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(userData)
    });
    return handleApiResponse(response);
  },

  getCurrentUser: async () => {
    // Auto-inject admin parent preview so /me returns parent_company
    // chrome (clinicName = parent name, etc.) when an internal admin
    // is previewing a parent admin's portal.
    const previewId = getAdminParentPreviewId();
    const url = previewId
      ? `${API_BASE_URL}/auth/me?adminPreviewParentCompanyId=${encodeURIComponent(previewId)}`
      : `${API_BASE_URL}/auth/me`;
    const requestOptions = {
      method: 'GET',
      headers: getAuthHeaders(),
      url,
    };
    const response = await fetch(url, {
      method: 'GET',
      headers: requestOptions.headers,
    });
    return handleApiResponse(response, requestOptions);
  },

  changePassword: async (currentPassword, newPassword) => {
    const response = await fetch(`${API_BASE_URL}/auth/change-password`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify({ currentPassword, newPassword })
    });
    return handleApiResponse(response);
  },

  hasSetPassword: async () => {
    const response = await fetch(`${API_BASE_URL}/auth/has-set-password`, {
      headers: getAuthHeaders()
    });
    return handleApiResponse(response);
  },

  // Public — no auth header. Backend always returns success (anti-enumeration).
  forgotPassword: async (email) => {
    const response = await fetch(`${API_BASE_URL}/auth/forgot-password`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email })
    });
    return handleApiResponse(response);
  },

  // Public — sets a new password from a reset token (from the emailed link).
  resetPassword: async (token, newPassword) => {
    const response = await fetch(`${API_BASE_URL}/auth/reset-password`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ token, newPassword })
    });
    return handleApiResponse(response);
  }
};

// =====================================================
// MAILBOX SYNC (Microsoft 365 OAuth) — Phase 2 connect/disconnect
// =====================================================
// Sync worker, matching, and timeline rendering arrive in subsequent
// phases (see docs/EMAIL_SYNC_BLUEPRINT.md). These three endpoints
// handle the connect-mailbox lifecycle only.
export const mailboxAPI = {
  // List the current user's mailbox connections + whether the feature
  // is enabled on the server (so the UI can hide the connect card on
  // environments without Microsoft OAuth configured).
  getStatus: async () => {
    const response = await fetch(`${API_BASE_URL}/mailbox/status`, {
      headers: getAuthHeaders()
    });
    return handleApiResponse(response);
  },

  // Ask the backend for the consent URL for the chosen provider. The
  // browser then navigates there; on success the provider redirects to
  // /api/mailbox/callback, which writes the connection and bounces back
  // to `returnTo`.
  getAuthUrl: async (returnTo: string, provider: 'GOOGLE' | 'MICROSOFT' = 'GOOGLE') => {
    const url = new URL(`${API_BASE_URL}/mailbox/auth-url`);
    if (returnTo) url.searchParams.set('returnTo', returnTo);
    url.searchParams.set('provider', provider);
    const response = await fetch(url.toString(), {
      headers: getAuthHeaders()
    });
    return handleApiResponse(response);
  },

  // Soft-disconnect: clears tokens server-side. To fully revoke our
  // app's access in Microsoft, the user must remove the consent at
  // myaccount.microsoft.com -> Privacy -> Apps and services.
  disconnect: async (connectionId: string) => {
    const response = await fetch(`${API_BASE_URL}/mailbox/${connectionId}`, {
      method: 'DELETE',
      headers: getAuthHeaders()
    });
    return handleApiResponse(response);
  }
};

// =====================================================
// EMAIL TIMELINE / BODY / ATTACHMENTS — Phase 5
// =====================================================
// Reads synced email logs for a client or lead and lazy-loads body/attachments.
export const emailsAPI = {
  /** Org-wide synced mail log (internal readers). params: { direction?, limit? } */
  getAll: async (params: { direction?: string; limit?: number } = {}) => {
    const query = new URLSearchParams();
    if (params.direction) query.append('direction', params.direction);
    if (params.limit) query.append('limit', String(params.limit));
    const response = await fetch(
      `${API_BASE_URL}/emails/all${query.toString() ? `?${query.toString()}` : ''}`,
      { headers: getAuthHeaders() }
    );
    return handleApiResponse(response);
  },

  getClientTimeline: async (clientUserId: string) => {
    const response = await fetch(
      `${API_BASE_URL}/emails/timeline/client/${encodeURIComponent(clientUserId)}`,
      { headers: getAuthHeaders() }
    );
    return handleApiResponse(response);
  },

  getLeadTimeline: async (leadId: string) => {
    const response = await fetch(
      `${API_BASE_URL}/emails/timeline/lead/${encodeURIComponent(leadId)}`,
      { headers: getAuthHeaders() }
    );
    return handleApiResponse(response);
  },

  getBody: async (emailMessageId: string) => {
    const response = await fetch(
      `${API_BASE_URL}/emails/${encodeURIComponent(emailMessageId)}/body`,
      { headers: getAuthHeaders() }
    );
    return handleApiResponse(response);
  },

  getAttachments: async (emailMessageId: string) => {
    const response = await fetch(
      `${API_BASE_URL}/emails/${encodeURIComponent(emailMessageId)}/attachments`,
      { headers: getAuthHeaders() }
    );
    return handleApiResponse(response);
  },

  // Returns a fully-qualified URL the browser can use directly. The
  // attachment endpoint streams binary; we don't run it through
  // handleApiResponse because we want the user's click to download
  // straight from the API.
  getAttachmentDownloadUrl: (emailMessageId: string, attachmentId: string): string =>
    `${API_BASE_URL}/emails/${encodeURIComponent(emailMessageId)}/attachments/${encodeURIComponent(attachmentId)}`,

  // Send a new email through a connected mailbox. payload shape:
  //   { connectionId, to: [{address,name?}], cc?, bcc?, subject, bodyHtml,
  //     replyToEmailMessageId? }
  // The backend handles threading (In-Reply-To/References) when
  // replyToEmailMessageId is provided.
  send: async (payload: any) => {
    const response = await fetch(`${API_BASE_URL}/emails/send`, {
      method: 'POST',
      headers: {
        ...getAuthHeaders(),
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(payload)
    });
    return handleApiResponse(response);
  }
};

export const clientAPI = {
  // Client management endpoints
  getSignupRequests: async (organization) => {
    const response = await fetch(`${API_BASE_URL}/clients/signup-requests?org=${organization}`, {
      headers: getAuthHeaders()
    });
    return handleApiResponse(response);
  },

  approveSignup: async (requestId, approvalData) => {
    const response = await fetch(`${API_BASE_URL}/clients/signup-requests/${requestId}/approve`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify(approvalData)
    });
    return handleApiResponse(response);
  },

  rejectSignup: async (requestId, reason) => {
    const response = await fetch(`${API_BASE_URL}/clients/signup-requests/${requestId}/reject`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify({ reason })
    });
    return handleApiResponse(response);
  },

  createClient: async (clientData) => {
    const response = await fetch(`${API_BASE_URL}/clients`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify(clientData)
    });
    return handleApiResponse(response);
  },

  getClients: async (organization) => {
    const response = await fetch(`${API_BASE_URL}/clients?org=${organization}`, {
      headers: getAuthHeaders()
    });
    return handleApiResponse(response);
  },

  updateClient: async (clientId, updateData) => {
    const response = await fetch(`${API_BASE_URL}/clients/${clientId}`, {
      method: 'PUT',
      headers: getAuthHeaders(),
      body: JSON.stringify(updateData)
    });
    return handleApiResponse(response);
  }
};

export const paymentAPI = {
  // Generate a fresh payment page link (replaces invoice creation)
  generatePaymentLink: async (orderId) => {
    const response = await fetch(`${API_BASE_URL}/payments/orders/${orderId}/generate-payment-link`, {
      method: 'POST',
      headers: getAuthHeaders()
    });
    return handleApiResponse(response);
  },

  // LEGACY: Invoice creation (kept for backward compatibility)
  createInvoice: async (invoiceData) => {
    const response = await fetch(`${API_BASE_URL}/payments/create-invoice`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify(invoiceData)
    });
    return handleApiResponse(response);
  },

  // Payment processing (for order history)
  processInvoicePayment: async (paymentData) => {
    const response = await fetch(`${API_BASE_URL}/payments/process-invoice-payment`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify(paymentData)
    });
    return handleApiResponse(response);
  },

  // Get invoice details
  getInvoiceDetails: async (orderId) => {
    const response = await fetch(`${API_BASE_URL}/payments/invoice/${orderId}`, {
      headers: getAuthHeaders()
    });
    return handleApiResponse(response);
  },

  // Save payment method to Tricera
  savePaymentMethod: async (customerId, paymentMethod) => {
    const response = await fetch(`${API_BASE_URL}/payments/save-method`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify({ customerId, paymentMethod })
    });
    return handleApiResponse(response);
  },

  // Get saved payment methods
  getPaymentMethods: async (customerId) => {
    const response = await fetch(`${API_BASE_URL}/payments/methods/${customerId}`, {
      headers: getAuthHeaders()
    });
    return handleApiResponse(response);
  },

  updateInvoiceTotals: async (orderId, payload) => {
    return await apiCall('PUT', `${API_BASE_URL}/payments/orders/${orderId}/invoice`, {
      body: JSON.stringify(payload)
    });
  },

  // Get saved cards for customer (Tricera)
  getSavedCards: async (customerEmail) => {
    const response = await fetch(`${API_BASE_URL}/payments/saved-cards?email=${encodeURIComponent(customerEmail)}`, {
      headers: getAuthHeaders()
    });
    return handleApiResponse(response);
  },

  // Process payment with card (new or saved)
  processPayment: async (paymentData) => {
    const response = await fetch(`${API_BASE_URL}/payments/process-payment`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify(paymentData)
    });
    return handleApiResponse(response);
  },

  // Manually link a Tricera invoice to an order
  linkTriceraInvoice: async (orderId, triceraInvoiceId) => {
    const response = await fetch(`${API_BASE_URL}/payments/orders/${orderId}/link-tricera`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify({ triceraInvoiceId })
    });
    return handleApiResponse(response);
  },

  // Retry creating a Tricera invoice for an order
  retryInvoiceCreation: async (orderId) => {
    const response = await fetch(`${API_BASE_URL}/payments/orders/${orderId}/retry-invoice`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify({})
    });
    return handleApiResponse(response);
  },

  // Sync payment status from Tricera for a specific order
  syncPaymentStatus: async (orderId) => {
    const response = await fetch(`${API_BASE_URL}/payments/orders/${orderId}/sync-payment-status`, {
      method: 'POST',
      headers: getAuthHeaders(),
    });
    return handleApiResponse(response);
  },

  // Sync all pending payment transactions
  syncAllPending: async () => {
    const response = await fetch(`${API_BASE_URL}/payments/sync-all-pending`, {
      method: 'POST',
      headers: getAuthHeaders(),
    });
    return handleApiResponse(response);
  }
};

export const orderAPI = {
  // Order management endpoints
  /**
   * Get orders - automatically includes clientId when admin is viewing as client
   */
  getOrders: async (filters = {}) => {
    const params = { ...filters };

    // Add clientId when viewing as client (admin perspective) - if not already set
    if (!params.clientId) {
      const perspectiveClientId = getPerspectiveClientId();
      if (perspectiveClientId) {
        params.clientId = perspectiveClientId;
      }
    }

    // Admin parent-company preview: scope orders across all the parent's
    // children when an internal admin clicked View As on a parent
    // company row. Backend ignores this for non-INTERNAL callers.
    if (!params.adminPreviewParentCompanyId) {
      const previewParentId = getAdminParentPreviewId();
      if (previewParentId) {
        params.adminPreviewParentCompanyId = previewParentId;
      }
    }

    const queryParams = new URLSearchParams(params).toString();
    return await apiCall('GET', `${API_BASE_URL}/orders?${queryParams}`);
  },

  createOrder: async (orderData) => {
    return await apiCall('POST', `${API_BASE_URL}/orders`, {
      body: JSON.stringify(orderData)
    });
  },

  updateOrderStatus: async (orderId, status, statusData = {}) => {
    return await apiCall('PATCH', `${API_BASE_URL}/orders/${orderId}/status`, {
      body: JSON.stringify({ status, ...statusData })
    });
  },

  assignOrder: async (orderId, assigneeId) => {
    return await apiCall('POST', `${API_BASE_URL}/orders/${orderId}/assign`, {
      body: JSON.stringify({ assigneeId })
    });
  },

  getOrderHistory: async (clientId) => {
    return await apiCall('GET', `${API_BASE_URL}/orders/client/${clientId}`);
  },

  getPaymentLink: async (orderId) => {
    return await apiCall('GET', `${API_BASE_URL}/orders/${orderId}/payment-link`);
  },

  getLabel: async (orderId) => {
    return await apiCall('GET', `${API_BASE_URL}/orders/${orderId}/label`);
  },

  updateLineItems: async (orderId, lineItemsData) => {
    return await apiCall('PUT', `${API_BASE_URL}/orders/${orderId}/line-items`, {
      body: JSON.stringify(lineItemsData)
    });
  },

  saveThirdPartyShipment: async (orderId, shipmentData) => {
    return await apiCall('PUT', `${API_BASE_URL}/orders/${orderId}/third-party-shipment`, {
      body: JSON.stringify(shipmentData)
    });
  },

  /**
   * Sync/backfill client inventory tracking for shipped/delivered orders
   * Creates tracking records for orders that were shipped without proper SKU allocation
   */
  syncInventory: async (orderId) => {
    return await apiCall('POST', `${API_BASE_URL}/orders/${orderId}/sync-inventory`);
  },

  // Rep order management
  placeOrderForClient: async (orderData) => {
    return await apiCall('POST', `${API_BASE_URL}/orders/rep/place-order`, {
      body: JSON.stringify(orderData)
    });
  },

  // Order change requests: request a change (delivery date / quantity / promo),
  // list requests for an order, and admin approve/deny.
  requestOrderChange: async (orderId, payload) => {
    return await apiCall('POST', `${API_BASE_URL}/orders/${orderId}/change-requests`, {
      body: JSON.stringify(payload)
    });
  },

  getOrderChangeRequests: async (orderId) => {
    return await apiCall('GET', `${API_BASE_URL}/orders/${orderId}/change-requests`);
  },

  approveOrderChange: async (orderId, requestId) => {
    return await apiCall('POST', `${API_BASE_URL}/orders/${orderId}/change-requests/${requestId}/approve`);
  },

  denyOrderChange: async (orderId, requestId, reason) => {
    return await apiCall('POST', `${API_BASE_URL}/orders/${orderId}/change-requests/${requestId}/deny`, {
      body: JSON.stringify({ reason })
    });
  },

  updateOrder: async (orderId, orderData) => {
    return await apiCall('PUT', `${API_BASE_URL}/orders/${orderId}/rep-edit`, {
      body: JSON.stringify(orderData)
    });
  },

  // ==========================================
  // ORDER PROCESSING ENHANCEMENTS - DEC 2025
  // ==========================================

  /**
   * Cancel order with reason and restore inventory
   */
  cancelOrder: async (orderId, reason) => {
    return await apiCall('PUT', `${API_BASE_URL}/orders/${orderId}/cancel-enhanced`, {
      body: JSON.stringify({ reason })
    });
  },

  /**
   * Duplicate an existing order
   */
  duplicateOrder: async (orderId) => {
    return await apiCall('POST', `${API_BASE_URL}/orders/${orderId}/duplicate`);
  },

  /**
   * Edit order date
   */
  editOrderDate: async (orderId, orderDate, reason) => {
    return await apiCall('PUT', `${API_BASE_URL}/orders/${orderId}/edit-date`, {
      body: JSON.stringify({ orderDate, reason })
    });
  },

  /**
   * Edit order number (for legacy orders)
   */
  editOrderNumber: async (orderId, newOrderNumber, reason) => {
    return await apiCall('PUT', `${API_BASE_URL}/orders/${orderId}/edit-order-number`, {
      body: JSON.stringify({ newOrderNumber, reason })
    });
  },

  /**
   * Edit order details (shipping, items, etc.)
   */
  editOrder: async (orderId, editData) => {
    return await apiCall('PUT', `${API_BASE_URL}/orders/${orderId}/edit`, {
      body: JSON.stringify(editData)
    });
  },

  /**
   * Update shipping cost with optional waiver
   */
  updateShippingCost: async (orderId, shippingData) => {
    return await apiCall('PUT', `${API_BASE_URL}/orders/${orderId}/shipping-cost`, {
      body: JSON.stringify(shippingData)
    });
  },

  /**
   * Apply promo code to order
   */
  applyPromoCode: async (orderId, promoCode) => {
    return await apiCall('PUT', `${API_BASE_URL}/orders/${orderId}/apply-promo`, {
      body: JSON.stringify({ promoCode })
    });
  },

  /**
   * Unpick/remove a tissue ID from order
   */
  unpickItem: async (orderId, unpickData) => {
    return await apiCall('POST', `${API_BASE_URL}/orders/${orderId}/unpick-item`, {
      body: JSON.stringify(unpickData)
    });
  },

  /**
   * Get allocated/picked tissue IDs for an order
   */
  getAllocatedSkus: async (orderId) => {
    return await apiCall('GET', `${API_BASE_URL}/orders/${orderId}/allocated-skus`);
  },

  /**
   * FIX 5: Permanently delete an order (only PENDING orders)
   */
  deleteOrder: async (orderId, reason) => {
    return await apiCall('DELETE', `${API_BASE_URL}/orders/${orderId}`, {
      body: JSON.stringify({ reason })
    });
  },

  /**
   * Get single order by ID
   */
  getOrder: async (orderId) => {
    return await apiCall('GET', `${API_BASE_URL}/orders/${orderId}`);
  }
};

export const inventoryAPI = {
  // Inventory management endpoints
  scanSKU: async (skuData) => {
    return await apiCall('POST', `${API_BASE_URL}/inventory/scan`, {
      body: JSON.stringify({
        sku: skuData.sku,
        orderId: skuData.orderId,
        scannedBy: skuData.scannedBy,
        timestamp: new Date().toISOString()
      })
    });
  },

  getInventoryLevels: async (params = {}) => {
    const query = new URLSearchParams();
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        query.append(key, value);
      }
    });
    const qs = query.toString();
    return await apiCall('GET', `${API_BASE_URL}/inventory${qs ? `?${qs}` : ''}`);
  },

  updateInventory: async (inventoryData) => {
    return await apiCall('POST', `${API_BASE_URL}/inventory`, {
      body: JSON.stringify(inventoryData)
    });
  },

  getMovementHistory: async (sku = null) => {
    const url = sku
      ? `${API_BASE_URL}/inventory/movements?sku=${sku}`
      : `${API_BASE_URL}/inventory/movements`;
    return await apiCall('GET', url);
  },

  getRecentMovements: async ({ organization, limit = 20 } = {}) => {
    const query = new URLSearchParams();
    if (organization) query.append('organization', organization);
    if (limit) query.append('limit', String(limit));
    return await apiCall('GET', `${API_BASE_URL}/inventory/movements/recent?${query.toString()}`);
  },

  getAnalyticsSummary: async ({ organization } = {}) => {
    const query = new URLSearchParams();
    if (organization) query.append('organization', organization);
    const qs = query.toString();
    return await apiCall('GET', `${API_BASE_URL}/inventory/analytics/summary${qs ? `?${qs}` : ''}`);
  },

  getSKUs: async (organization, limit = 500, params = {}) => {
    const query = new URLSearchParams();
    if (organization) query.append('organization', organization);
    if (limit) query.append('limit', String(limit));
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        query.append(key, value);
      }
    });
    const queryString = query.toString();
    const url = `${API_BASE_URL}/inventory-skus${queryString ? `?${queryString}` : ''}`;
    return await apiCall('GET', url);
  },

  getBatchById: async (inventoryId) => {
    return await apiCall('GET', `${API_BASE_URL}/inventory/${inventoryId}`);
  },

  updateBatchStatus: async (inventoryId, payload) => {
    return await apiCall('PATCH', `${API_BASE_URL}/inventory/batches/${inventoryId}`, {
      body: JSON.stringify(payload)
    });
  },

  bulkUpdateSkus: async (payload) => {
    return await apiCall('POST', `${API_BASE_URL}/inventory-skus/bulk-update`, {
      body: JSON.stringify(payload)
    });
  },

  bulkDiscardSkus: async (skuIds, reason) => {
    return await apiCall('POST', `${API_BASE_URL}/inventory-skus/bulk-discard`, {
      body: JSON.stringify({ skuIds, reason })
    });
  },

  recoverOrphanedPicks: async (tissueIds, reason, apply = false) => {
    return await apiCall('POST', `${API_BASE_URL}/inventory-skus/recover-orphaned-picks`, {
      body: JSON.stringify({ tissueIds, reason, apply })
    });
  },

  uploadBatchCertificate: async (inventoryId, file) => {
    const formData = new FormData();
    formData.append('file', file);
    const headers = { ...getAuthHeaders() };
    delete headers['Content-Type'];

    const response = await fetch(`${API_BASE_URL}/inventory/batches/${inventoryId}/certificate`, {
      method: 'POST',
      headers,
      body: formData,
      credentials: 'include'
    });
    return handleApiResponse(response);
  },

  uploadBatchCertificates: async (inventoryId, files) => {
    const formData = new FormData();
    Array.from(files).forEach(file => {
      formData.append('files', file);
    });
    const headers = { ...getAuthHeaders() };
    delete headers['Content-Type'];

    const response = await fetch(`${API_BASE_URL}/inventory/batches/${inventoryId}/certificates`, {
      method: 'POST',
      headers,
      body: formData,
      credentials: 'include'
    });
    return handleApiResponse(response);
  },

  deleteBatchCertificate: async (inventoryId, certificateId) => {
    return await apiCall('DELETE', `${API_BASE_URL}/inventory/batches/${inventoryId}/certificates/${certificateId}`);
  },

  updateBatchDates: async (inventoryId, dates) => {
    return await apiCall('PATCH', `${API_BASE_URL}/inventory/batches/${inventoryId}/dates`, {
      body: JSON.stringify(dates)
    });
  },

  getBatchCertificate: async (inventoryId) => {
    return await apiCall('GET', `${API_BASE_URL}/inventory/batches/${inventoryId}/certificate`);
  },

  deleteBatch: async (inventoryId) => {
    return await apiCall('DELETE', `${API_BASE_URL}/inventory/batches/${inventoryId}`);
  }
};

export const inventoryViewsAPI = {
  getSavedViews: async (organization: string) => {
    return apiCall('GET', `${API_BASE_URL}/inventory-views/saved?organization=${organization}`);
  },
  createSavedView: async (data: any) => {
    return apiCall('POST', `${API_BASE_URL}/inventory-views/saved`, { body: JSON.stringify(data) });
  },
  deleteSavedView: async (id: string) => {
    return apiCall('DELETE', `${API_BASE_URL}/inventory-views/saved/${id}`);
  },
  executeView: async (config: any) => {
    return apiCall('POST', `${API_BASE_URL}/inventory-views/execute`, { body: JSON.stringify(config) });
  },
};

export const productAPI = {
  getProducts: async (options = {}) => {
    const query = new URLSearchParams();
    if (options.includeInactive) query.append('includeInactive', 'true');
    if (options.limit) query.append('limit', String(options.limit));
    if (options.offset) query.append('offset', String(options.offset));
    if (options.page) query.append('page', String(options.page));
    if (options.organization) query.append('organization', options.organization);
    if (options.search) query.append('search', options.search);
    if (options.category) query.append('category', options.category);
    if (options.status) query.append('status', options.status);
    // Admin parent-company preview: when an internal admin clicked
    // View As on a parent company, we inject the param so the backend
    // applies the same showInShop / status filters a real parent admin
    // (or client) would get -- otherwise hidden products leak into
    // the preview.
    const previewParentId = getAdminParentPreviewId();
    if (previewParentId && !options.adminPreviewParentCompanyId) {
      query.append('adminPreviewParentCompanyId', previewParentId);
    } else if (options.adminPreviewParentCompanyId) {
      query.append('adminPreviewParentCompanyId', options.adminPreviewParentCompanyId);
    }
    const queryString = query.toString();
    const url = `${API_BASE_URL}/products${queryString ? `?${queryString}` : ''}`;
    return await apiCall('GET', url);
  },

  getProduct: async (productId) => {
    return await apiCall('GET', `${API_BASE_URL}/products/${productId}`);
  },

  createProduct: async (productData) => {
    return await apiCall('POST', `${API_BASE_URL}/products`, {
      body: JSON.stringify(productData)
    });
  },

  updateProduct: async (productId, productData) => {
    return await apiCall('PATCH', `${API_BASE_URL}/products/${productId}`, {
      body: JSON.stringify(productData)
    });
  },

  updateStatus: async (productId, status) => {
    return await apiCall('PATCH', `${API_BASE_URL}/products/${productId}/status`, {
      body: JSON.stringify({ status })
    });
  },

  uploadProductImage: async (productId, file) => {
    const formData = new FormData();
    formData.append('image', file);
    const headers = getAuthHeaders();
    delete headers['Content-Type'];

    const response = await fetch(`${API_BASE_URL}/products/${productId}/image`, {
      method: 'POST',
      headers,
      body: formData,
      credentials: 'include'
    });
    return handleApiResponse(response);
  },

  deleteProduct: async (productId) => {
    return await apiCall('DELETE', `${API_BASE_URL}/products/${productId}`);
  },

  deleteProductImage: async (productId) => {
    return await apiCall('DELETE', `${API_BASE_URL}/products/${productId}/image`);
  },

  // Pricing Tiers
  getPricingTiers: async (productId, tierGroupId) => {
    const query = tierGroupId ? `?tierGroupId=${tierGroupId}` : '';
    return await apiCall('GET', `${API_BASE_URL}/products/${productId}/pricing-tiers${query}`);
  },

  createPricingTier: async (productId, tierData) => {
    return await apiCall('POST', `${API_BASE_URL}/products/${productId}/pricing-tiers`, {
      body: JSON.stringify(tierData)
    });
  },

  updatePricingTier: async (productId, tierId, tierData) => {
    return await apiCall('PUT', `${API_BASE_URL}/products/${productId}/pricing-tiers/${tierId}`, {
      body: JSON.stringify(tierData)
    });
  },

  deletePricingTier: async (productId, tierId) => {
    return await apiCall('DELETE', `${API_BASE_URL}/products/${productId}/pricing-tiers/${tierId}`);
  },

  savePricingTiersBulk: async (productId, tiers) => {
    return await apiCall('POST', `${API_BASE_URL}/products/${productId}/pricing-tiers/bulk`, {
      body: JSON.stringify({ tiers })
    });
  },

  // Price History
  getPriceHistory: async (productId) => {
    return await apiCall('GET', `${API_BASE_URL}/products/${productId}/price-history`);
  },

  // Pricing Tier Groups
  getPricingTierGroups: async (productId) => {
    return await apiCall('GET', `${API_BASE_URL}/products/${productId}/pricing-tier-groups`);
  },

  createPricingTierGroup: async (productId, groupData) => {
    return await apiCall('POST', `${API_BASE_URL}/products/${productId}/pricing-tier-groups`, {
      body: JSON.stringify(groupData)
    });
  },

  updatePricingTierGroup: async (productId, groupId, groupData) => {
    return await apiCall('PUT', `${API_BASE_URL}/products/${productId}/pricing-tier-groups/${groupId}`, {
      body: JSON.stringify(groupData)
    });
  },

  deletePricingTierGroup: async (productId, groupId) => {
    return await apiCall('DELETE', `${API_BASE_URL}/products/${productId}/pricing-tier-groups/${groupId}`);
  },

  getPricingTierGroupTiers: async (productId, groupId) => {
    return await apiCall('GET', `${API_BASE_URL}/products/${productId}/pricing-tier-groups/${groupId}/tiers`);
  },

  savePricingTierGroupTiersBulk: async (productId, groupId, tiers) => {
    return await apiCall('POST', `${API_BASE_URL}/products/${productId}/pricing-tier-groups/${groupId}/tiers/bulk`, {
      body: JSON.stringify({ tiers })
    });
  },

  // CMS - Get product by slug (for client product pages)
  getProductBySlug: async (slug) => {
    return await apiCall('GET', `${API_BASE_URL}/products/by-slug/${slug}`);
  },

  // CMS - Upload marketing carousel images
  uploadMarketingImages: async (productId, files) => {
    const formData = new FormData();
    for (const file of files) {
      formData.append('images', file);
    }
    const headers = getAuthHeaders();
    delete headers['Content-Type'];
    const response = await fetch(`${API_BASE_URL}/products/${productId}/marketing-images`, {
      method: 'POST',
      headers,
      body: formData,
      credentials: 'include'
    });
    return handleApiResponse(response);
  },

  // CMS - Delete a marketing carousel image by index
  deleteMarketingImage: async (productId, index) => {
    return await apiCall('DELETE', `${API_BASE_URL}/products/${productId}/marketing-images/${index}`);
  },
};

// Global Settings API (pricing, prompt pay, etc.)
export const settingsAPI = {
  getSettings: async (organization = 'EPL') => {
    return await apiCall('GET', `${API_BASE_URL}/settings?organization=${organization}`);
  },

  getAllSettings: async () => {
    return await apiCall('GET', `${API_BASE_URL}/settings/all`);
  },

  updateSettings: async (organization, settingsData) => {
    return await apiCall('PUT', `${API_BASE_URL}/settings/${organization}`, {
      body: JSON.stringify(settingsData)
    });
  },

  // Pricing Tier Groups (organization-level, for customer assignment dropdowns)
  getPricingTierGroups: async (organization = 'EPL') => {
    return await apiCall('GET', `${API_BASE_URL}/settings/pricing-tier-groups?organization=${organization}`);
  },

  getPricingTierGroupCustomers: async (groupId) => {
    return await apiCall('GET', `${API_BASE_URL}/settings/pricing-tier-groups/${groupId}/customers`);
  },

  calculatePricing: async (productId, quantity, includePromptPay = true, clientUserId = null) => {
    const params = new URLSearchParams({
      productId,
      quantity: String(quantity),
      includePromptPay: String(includePromptPay)
    });
    // Price for a specific client so volume-group discounts match the actual order.
    if (clientUserId) params.append('clientUserId', String(clientUserId));
    return await apiCall('GET', `${API_BASE_URL}/settings/pricing-calculator?${params}`);
  },

  // Shipping Rates Management
  getShippingRates: async (organization = 'EPL', activeOnly = true) => {
    const params = new URLSearchParams({
      organization,
      activeOnly: String(activeOnly)
    });
    return await apiCall('GET', `${API_BASE_URL}/settings/shipping-rates?${params}`);
  },

  // Get active shipping rates formatted for checkout/ordering dropdowns
  getActiveShippingRates: async (organization = 'EPL') => {
    return await apiCall('GET', `${API_BASE_URL}/settings/shipping-rates/active?organization=${organization}`);
  },

  syncShippingRates: async (organization = 'EPL') => {
    return await apiCall('POST', `${API_BASE_URL}/settings/shipping-rates/sync`, {
      body: JSON.stringify({ organization })
    });
  },

  updateShippingRate: async (rateId, rateData) => {
    return await apiCall('PUT', `${API_BASE_URL}/settings/shipping-rates/${rateId}`, {
      body: JSON.stringify(rateData)
    });
  },

  bulkUpdateShippingRates: async (rates) => {
    return await apiCall('PUT', `${API_BASE_URL}/settings/shipping-rates/bulk`, {
      body: JSON.stringify({ rates })
    });
  },

  deleteShippingRate: async (rateId) => {
    return await apiCall('DELETE', `${API_BASE_URL}/settings/shipping-rates/${rateId}`);
  },

  // MD User Setting
  getMdEmail: async () => {
    return await apiCall('GET', `${API_BASE_URL}/settings/md-email`);
  },

  updateMdUser: async (userId) => {
    return await apiCall('PUT', `${API_BASE_URL}/settings/md-email`, {
      body: JSON.stringify({ userId })
    });
  }
};

// =====================================================
// VET INTRO PRICING API
// Universal "first N vials per vet at $X" rule plus the per-vet exception
// table. Backend lives under /api/vet-intro.
// =====================================================
export const vetIntroAPI = {
  /**
   * GET /api/vet-intro/:contactId/status
   * Returns the current eligibility/remaining-vials status for a vet contact.
   */
  vetIntroStatus: async (contactId, organization = 'EPL') => {
    const params = new URLSearchParams({ organization });
    return await apiCall(
      'GET',
      `${API_BASE_URL}/vet-intro/${encodeURIComponent(contactId)}/status?${params.toString()}`
    );
  },

  /**
   * Exception CRUD — see backend/src/routes/vetIntroPricing.ts.
   * Exceptions are keyed by vet identity (license number, email fallback)
   * and grant additional vials beyond the global per-vet threshold.
   */
  vetIntroExceptions: {
    list: async (organization = 'EPL') => {
      const params = new URLSearchParams({ organization });
      return await apiCall('GET', `${API_BASE_URL}/vet-intro/exceptions?${params.toString()}`);
    },
    create: async (payload) => {
      return await apiCall('POST', `${API_BASE_URL}/vet-intro/exceptions`, {
        body: JSON.stringify(payload),
      });
    },
    update: async (exceptionId, payload) => {
      return await apiCall('PATCH', `${API_BASE_URL}/vet-intro/exceptions/${encodeURIComponent(exceptionId)}`, {
        body: JSON.stringify(payload),
      });
    },
    deactivate: async (exceptionId) => {
      return await apiCall('DELETE', `${API_BASE_URL}/vet-intro/exceptions/${encodeURIComponent(exceptionId)}`);
    },
  },
};

export const shippingAPI = {
  // Shipping and fulfillment endpoints
  createShipment: async (shipmentData) => {
    const response = await fetch(`${API_BASE_URL}/shipping/create`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify(shipmentData)
    });
    return handleApiResponse(response);
  },

  printLabel: async (orderId, labelData) => {
    const response = await fetch(`${API_BASE_URL}/shipping/label/${orderId}`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify(labelData)
    });
    return handleApiResponse(response);
  },

  trackShipment: async (trackingNumber) => {
    const response = await fetch(`${API_BASE_URL}/shipping/track/${trackingNumber}`, {
      headers: getAuthHeaders()
    });
    return handleApiResponse(response);
  },

  getShippingRates: async (packageData) => {
    const response = await fetch(`${API_BASE_URL}/shipping/rates`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify(packageData)
    });
    return handleApiResponse(response);
  }
};

export const analyticsAPI = {
  // Analytics and reporting endpoints
  getKPIMetrics: async (organization, dateRange) => {
    const response = await fetch(`${API_BASE_URL}/analytics/kpis?org=${organization}&range=${dateRange}`, {
      headers: getAuthHeaders()
    });
    return handleApiResponse(response);
  },

  getClientAnalytics: async (organization) => {
    const response = await fetch(`${API_BASE_URL}/analytics/clients?org=${organization}`, {
      headers: getAuthHeaders()
    });
    return handleApiResponse(response);
  },

  getOrderAnalytics: async (filters) => {
    const queryParams = new URLSearchParams(filters).toString();
    const response = await fetch(`${API_BASE_URL}/analytics/orders?${queryParams}`, {
      headers: getAuthHeaders()
    });
    return handleApiResponse(response);
  }
};

export const complianceAPI = {
  // HIPAA/FDA compliance endpoints
  logAuditEvent: async (eventData) => {
    const response = await fetch(`${API_BASE_URL}/compliance/audit`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify({
        ...eventData,
        timestamp: new Date().toISOString(),
        ipAddress: 'client-ip',  // Will be populated by backend
        userAgent: navigator.userAgent
      })
    });
    return handleApiResponse(response);
  },

  getAuditLogs: async (filters) => {
    const queryParams = new URLSearchParams(filters).toString();
    const response = await fetch(`${API_BASE_URL}/compliance/audit-logs?${queryParams}`, {
      headers: getAuthHeaders()
    });
    return handleApiResponse(response);
  },

  generateComplianceReport: async (reportType, dateRange) => {
    const response = await fetch(`${API_BASE_URL}/compliance/reports/${reportType}`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify({ dateRange })
    });
    return handleApiResponse(response);
  }
};

// Email Log (admin "Email Log" tab). Replaces the Operations tab. Super-admin
// gated server-side; UI hides the nav item for non-super-admins as well.
export const emailLogAPI = {
  list: async (params: Record<string, string | number | undefined> = {}) => {
    const search = new URLSearchParams();
    Object.entries(params).forEach(([k, v]) => {
      if (v !== undefined && v !== null && String(v).length > 0) search.append(k, String(v));
    });
    const response = await fetch(`${API_BASE_URL}/email-log?${search.toString()}`, {
      headers: getAuthHeaders(),
    });
    return handleApiResponse(response);
  },
  get: async (id: string) => {
    const response = await fetch(`${API_BASE_URL}/email-log/${id}`, {
      headers: getAuthHeaders(),
    });
    return handleApiResponse(response);
  },
  recipients: async (params: Record<string, string | number | undefined> = {}) => {
    const search = new URLSearchParams();
    Object.entries(params).forEach(([k, v]) => {
      if (v !== undefined && v !== null && String(v).length > 0) search.append(k, String(v));
    });
    const response = await fetch(`${API_BASE_URL}/email-log/recipients?${search.toString()}`, {
      headers: getAuthHeaders(),
    });
    return handleApiResponse(response);
  },
  bulkSend: async (payload: {
    recipients: string[];
    subject: string;
    html: string;
    organization?: 'EPL' | 'BARE';
    cc?: string[];
  }) => {
    const response = await fetch(`${API_BASE_URL}/email-log/bulk-send`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify(payload),
    });
    return handleApiResponse(response);
  },
  // Automated-email on/off controls (super-admin only).
  getAutomation: async () => {
    const response = await fetch(`${API_BASE_URL}/email-log/automation`, {
      headers: getAuthHeaders(),
    });
    return handleApiResponse(response);
  },
  setAutomation: async (key: string, enabled: boolean) => {
    const response = await fetch(`${API_BASE_URL}/email-log/automation/${encodeURIComponent(key)}`, {
      method: 'PATCH',
      headers: getAuthHeaders(),
      body: JSON.stringify({ enabled }),
    });
    return handleApiResponse(response);
  },
  previewAutomation: async (key: string) => {
    const response = await fetch(`${API_BASE_URL}/email-log/automation/${encodeURIComponent(key)}/preview`, {
      headers: getAuthHeaders(),
    });
    return handleApiResponse(response);
  },
};

// Error handling and token refresh
export const setupAPIInterceptors = (): void => {
  // Intercept all API calls to handle token refresh
  const originalFetch = window.fetch;
  let refreshPromise: Promise<string | null> | null = null;

  // Shared refresh function that deduplicates concurrent refresh attempts
  const doRefresh = (): Promise<string | null> => {
    if (refreshPromise) return refreshPromise;

    refreshPromise = (async () => {
      try {
        const refreshTokenVal = localStorage.getItem('refreshToken');
        if (!refreshTokenVal) throw new Error('No refresh token');

        const refreshResp = await originalFetch(`${API_BASE_URL}/auth/refresh`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ refreshToken: refreshTokenVal })
        });

        if (!refreshResp.ok) throw new Error('Refresh failed');

        const refreshData = await refreshResp.json();
        const newAccessToken = refreshData?.accessToken || refreshData?.token;
        if (newAccessToken) {
          localStorage.setItem('authToken', newAccessToken);
          return newAccessToken;
        }
        throw new Error('No token in refresh response');
      } catch (err) {
        // Refresh failed — clear tokens, redirect to login
        localStorage.removeItem('authToken');
        localStorage.removeItem('refreshToken');
        window.location.href = '#/login';
        throw err;
      } finally {
        refreshPromise = null;
      }
    })();

    return refreshPromise;
  };

  window.fetch = async (...args) => {
    try {
      const response = await originalFetch(...args);

      // Handle token expiration — skip for auth endpoints to prevent infinite loop
      const url = typeof args[0] === 'string' ? args[0] : args[0]?.url || '';
      const isAuthEndpoint = url.includes('/auth/refresh') || url.includes('/auth/login') || url.includes('/auth/logout');

      if (response.status === 401 && !isAuthEndpoint) {
        try {
          // All concurrent 401s share the same refresh promise
          const newAccessToken = await doRefresh();

          // Retry original request with new token
          const [reqUrl, optionsArg] = args;
          const options = { ...(optionsArg || {}) };
          options.headers = { ...(options.headers || {}) };
          if (newAccessToken) {
            (options.headers as Record<string, string>)['Authorization'] = `Bearer ${newAccessToken}`;
          }
          return originalFetch(reqUrl, options);
        } catch {
          throw new Error('Session expired');
        }
      }

      return response;
    } catch (error) {
      logger.error('API Error:', error);
      throw error;
    }
  };
};

// Admin API functions
export const adminAPI = {
  // User Management API
  getUsers: (params = {}) => {
    const query = new URLSearchParams();
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        query.append(key, value);
      }
    });
    const url = `${API_BASE_URL}/admin/users${query.toString() ? `?${query.toString()}` : ''}`;
    return apiCall('GET', url);
  },
  getUserById: (userId) => {
    // Add timestamp to prevent caching
    return apiCall('GET', `${API_BASE_URL}/admin/users/${userId}?t=${Date.now()}`);
  },
  createUser: async (userData) => {
    const headers = getAuthHeaders();
    logger.log('Sending request to create user');
    logger.log('Headers:', {
      ...headers,
      Authorization: headers.Authorization ? headers.Authorization.substring(0, 30) + '...' : 'MISSING'
    });
    logger.log('URL:', `${API_BASE_URL}/admin/users`);
    
    const response = await fetch(`${API_BASE_URL}/admin/users`, {
      method: 'POST',
      headers: headers,
      body: JSON.stringify(userData)
    });
    return handleApiResponse(response);
  },

  updateUser: async (userId, userData) => {
    const response = await fetch(`${API_BASE_URL}/admin/users/${userId}`, {
      method: 'PATCH',
      headers: getAuthHeaders(),
      body: JSON.stringify(userData)
    });
    return handleApiResponse(response);
  },

  changeUserPassword: async (userId, newPassword) => {
    const response = await fetch(`${API_BASE_URL}/admin/users/${userId}/change-password`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify({ newPassword })
    });
    return handleApiResponse(response);
  },

  updateUserStatus: async (userId, status, reason, licenseExpiresAt) => {
    return await apiCall('PATCH', `${API_BASE_URL}/users/${userId}/status`, {
      body: JSON.stringify({ status, reason, licenseExpiresAt })
    });
  },

  deleteUser: async (userId, permanent = false) => {
    const url = `${API_BASE_URL}/admin/users/${userId}${permanent ? '?permanent=true' : ''}`;
    const response = await fetch(url, {
      method: 'DELETE',
      headers: getAuthHeaders()
    });
    return handleApiResponse(response);
  },

  getUserStats: async () => {
    const response = await fetch(`${API_BASE_URL}/admin/users/stats`, {
      headers: getAuthHeaders()
    });
    return handleApiResponse(response);
  },

  // Lead Management
  getAllLeads: async (params = {}) => {
    const query = new URLSearchParams();
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        query.append(key, value);
      }
    });
    const url = `${API_BASE_URL}/leads/admin/all${query.toString() ? `?${query.toString()}` : ''}`;
    const response = await fetch(url, {
      headers: getAuthHeaders()
    });
    return handleApiResponse(response);
  },

  getPendingLeads: async () => {
    const response = await fetch(`${API_BASE_URL}/leads/admin/pending-approval`, {
      headers: getAuthHeaders()
    });
    return handleApiResponse(response);
  },

  getAllLoginRequests: async (params = {}) => {
    const query = new URLSearchParams();
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        query.append(key, value);
      }
    });
    const url = `${API_BASE_URL}/leads/admin/login-requests${query.toString() ? `?${query.toString()}` : ''}`;
    const response = await fetch(url, {
      headers: getAuthHeaders()
    });
    return handleApiResponse(response);
  },

  getAllLeadContacts: async () => {
    const response = await fetch(`${API_BASE_URL}/leads/admin/contacts`, {
      headers: getAuthHeaders()
    });
    return handleApiResponse(response);
  },

  convertLeadToClient: async (leadId, data) => {
    const response = await fetch(`${API_BASE_URL}/leads/${leadId}/convert-to-client`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify(data)
    });
    return handleApiResponse(response);
  },

  declineLead: async (leadId) => {
    const response = await fetch(`${API_BASE_URL}/leads/${leadId}`, {
      method: 'PUT',
      headers: getAuthHeaders(),
      body: JSON.stringify({ status: 'CLOSED_LOST' })
    });
    return handleApiResponse(response);
  },

  // Invoice Management
  getInvoices: async (params = {}) => {
    const query = new URLSearchParams();
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        query.append(key, value);
      }
    });
    const url = `${API_BASE_URL}/admin/invoices${query.toString() ? `?${query.toString()}` : ''}`;
    const response = await fetch(url, { headers: getAuthHeaders() });
    return handleApiResponse(response);
  },

  getInvoiceStats: async (params = {}) => {
    const query = new URLSearchParams();
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        query.append(key, value);
      }
    });
    const url = `${API_BASE_URL}/admin/invoices/stats${query.toString() ? `?${query.toString()}` : ''}`;
    const response = await fetch(url, { headers: getAuthHeaders() });
    return handleApiResponse(response);
  },

  getInvoice: async (invoiceId) => {
    const response = await fetch(`${API_BASE_URL}/admin/invoices/${invoiceId}`, {
      headers: getAuthHeaders()
    });
    return handleApiResponse(response);
  },

  adjustInvoice: async (invoiceId, data) => {
    const response = await fetch(`${API_BASE_URL}/admin/invoices/${invoiceId}/adjust`, {
      method: 'PUT',
      headers: getAuthHeaders(),
      body: JSON.stringify(data)
    });
    return handleApiResponse(response);
  },

  cancelBilling: async (invoiceId, reason) => {
    const response = await fetch(`${API_BASE_URL}/admin/invoices/${invoiceId}/cancel-billing`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify({ reason }),
    });
    return handleApiResponse(response);
  },

  flagInvoice: async (invoiceId, data) => {
    const response = await fetch(`${API_BASE_URL}/admin/invoices/${invoiceId}/flag`, {
      method: 'PUT',
      headers: getAuthHeaders(),
      body: JSON.stringify(data)
    });
    return handleApiResponse(response);
  },

  recordInvoicePayment: async (invoiceId, data) => {
    const response = await fetch(`${API_BASE_URL}/admin/invoices/${invoiceId}/record-payment`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify(data)
    });
    return handleApiResponse(response);
  },

  syncInvoiceTricera: async (invoiceId) => {
    const response = await fetch(`${API_BASE_URL}/admin/invoices/${invoiceId}/sync-tricera`, {
      method: 'POST',
      headers: getAuthHeaders()
    });
    return handleApiResponse(response);
  },

  bulkSyncInvoicesTricera: async () => {
    const response = await fetch(`${API_BASE_URL}/admin/invoices/bulk-sync-tricera`, {
      method: 'POST',
      headers: getAuthHeaders()
    });
    return handleApiResponse(response);
  },

  pushInvoiceToTricera: async (invoiceId, data) => {
    const response = await fetch(`${API_BASE_URL}/admin/invoices/${invoiceId}/push-to-tricera`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify(data)
    });
    return handleApiResponse(response);
  },

  voidInvoicePayment: async (invoiceId, reason) => {
    const response = await fetch(`${API_BASE_URL}/admin/invoices/${invoiceId}/payment`, {
      method: 'DELETE',
      headers: getAuthHeaders(),
      body: JSON.stringify({ reason })
    });
    return handleApiResponse(response);
  },

  // ========== Invoice Line Items Management ==========

  /**
   * Get all line items for an invoice
   */
  getInvoiceLineItems: async (invoiceId) => {
    const response = await fetch(`${API_BASE_URL}/admin/invoices/${invoiceId}/line-items`, {
      headers: getAuthHeaders()
    });
    return handleApiResponse(response);
  },

  /**
   * Update a line item (amount, visibility, description)
   */
  updateLineItem: async (invoiceId, lineItemId, data) => {
    const response = await fetch(`${API_BASE_URL}/admin/invoices/${invoiceId}/line-items/${lineItemId}`, {
      method: 'PUT',
      headers: getAuthHeaders(),
      body: JSON.stringify(data)
    });
    return handleApiResponse(response);
  },

  /**
   * Add a new line item (adjustment, credit, custom charge)
   */
  addLineItem: async (invoiceId, data) => {
    const response = await fetch(`${API_BASE_URL}/admin/invoices/${invoiceId}/line-items`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify(data)
    });
    return handleApiResponse(response);
  },

  /**
   * Remove a line item
   */
  removeLineItem: async (invoiceId, lineItemId, reason) => {
    const response = await fetch(`${API_BASE_URL}/admin/invoices/${invoiceId}/line-items/${lineItemId}`, {
      method: 'DELETE',
      headers: getAuthHeaders(),
      body: JSON.stringify({ reason })
    });
    return handleApiResponse(response);
  },

  /**
   * Recalculate invoice totals from visible line items
   */
  recalculateInvoice: async (invoiceId, reason) => {
    const response = await fetch(`${API_BASE_URL}/admin/invoices/${invoiceId}/recalculate`, {
      method: 'PUT',
      headers: getAuthHeaders(),
      body: JSON.stringify({ reason })
    });
    return handleApiResponse(response);
  },

  /**
   * Toggle a specific discount type on/off
   */
  toggleDiscount: async (invoiceId, discountType, visible, reason) => {
    const response = await fetch(`${API_BASE_URL}/admin/invoices/${invoiceId}/toggle-discount`, {
      method: 'PUT',
      headers: getAuthHeaders(),
      body: JSON.stringify({ discountType, visible, reason })
    });
    return handleApiResponse(response);
  },

  /**
   * Generate invoice line items from order data (for orders missing them)
   */
  generateLineItems: async (invoiceId, force = false) => {
    const response = await fetch(`${API_BASE_URL}/admin/invoices/${invoiceId}/generate-line-items`, {
      method: 'POST',
      headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
      body: JSON.stringify({ force }),
    });
    return handleApiResponse(response);
  },

  /**
   * Update invoice fields (addresses, PO#, shipping info, etc.) with audit trail
   */
  updateInvoiceFields: async (invoiceId, data) => {
    const response = await fetch(`${API_BASE_URL}/admin/invoices/${invoiceId}/update-fields`, {
      method: 'PUT',
      headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    });
    return handleApiResponse(response);
  },

  /**
   * Get audit history timeline for an invoice
   */
  getInvoiceAuditHistory: async (invoiceId) => {
    const response = await fetch(`${API_BASE_URL}/admin/invoices/${invoiceId}/audit-history`, {
      headers: getAuthHeaders(),
    });
    return handleApiResponse(response);
  },

  /**
   * Send/resend invoice email to client + AP contacts
   */
  sendInvoice: async (invoiceId, additionalEmails = []) => {
    const response = await fetch(`${API_BASE_URL}/admin/invoices/${invoiceId}/send-invoice`, {
      method: 'POST',
      headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
      body: JSON.stringify({ additionalEmails }),
    });
    return handleApiResponse(response);
  },

  /**
   * Manually apply a promo code to an existing invoice
   */
  applyPromoCode: async (invoiceId, promoCode) => {
    const response = await fetch(`${API_BASE_URL}/admin/invoices/${invoiceId}/apply-promo`, {
      method: 'POST',
      headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
      body: JSON.stringify({ promoCode }),
    });
    return handleApiResponse(response);
  },

  /**
   * Get consolidated payment data for an invoice
   */
  getInvoicePayments: async (invoiceId) => {
    const response = await fetch(`${API_BASE_URL}/admin/invoices/${invoiceId}/payments`, {
      headers: getAuthHeaders(),
    });
    return handleApiResponse(response);
  },

  /**
   * Get all dashboard KPI data in one call
   */
  getDashboardData: () => {
    return apiCall('GET', `${API_BASE_URL}/admin/dashboard-data`);
  },

  /**
   * Attention-badge counts for the internal sidebar (per nav item: {count, severity}).
   * Only items with a non-empty action queue are returned.
   */
  getSidebarBadges: (organization?: string) => {
    const qs = organization ? `?organization=${encodeURIComponent(organization)}` : '';
    return apiCall('GET', `${API_BASE_URL}/admin/sidebar-badges${qs}`);
  },

  clearReviewFlag: (userId: string) => {
    return apiCall('PATCH', `${API_BASE_URL}/admin/users/${userId}/clear-review-flag`);
  },
};

// ShipStation API
export const resourcesAPI = {
  /**
   * Upload a resource (Admin only)
   * @param {File} file - The file to upload
   * @param {string} visibility - "client" | "rep" | "both" (default: "both")
   */
  uploadResource: async (file, visibility = 'both', name?: string, extras?: { categoryId?: string | null; audience?: string | null; productTagIds?: string[]; sortOrder?: number; announce?: boolean }) => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('visibility', visibility);
    if (name) formData.append('name', name);
    if (extras?.categoryId) formData.append('categoryId', extras.categoryId);
    if (extras?.audience) formData.append('audience', extras.audience);
    if (extras?.productTagIds?.length) formData.append('productTagIds', JSON.stringify(extras.productTagIds));
    if (extras?.sortOrder !== undefined) formData.append('sortOrder', String(extras.sortOrder));
    if (extras?.announce) formData.append('announce', 'true');

    const token = localStorage.getItem('authToken');
    const response = await fetch(`${API_BASE_URL}/resources`, {
      method: 'POST',
      headers: {
        'Authorization': token ? `Bearer ${token}` : '',
        'X-Client-Version': '2.0.0',
        'X-HIPAA-Compliant': 'true'
      },
      body: formData
    });
    return handleApiResponse(response);
  },

  /**
   * List resources (filtered by caller's role automatically)
   */
  listResources: async () => {
    const requestOptions = {
      method: 'GET',
      headers: getAuthHeaders(),
      url: `${API_BASE_URL}/resources`
    };
    const response = await fetch(requestOptions.url, {
      method: requestOptions.method,
      headers: requestOptions.headers
    });
    return handleApiResponse(response, requestOptions);
  },

  /**
   * List resources for reps
   */
  listRepResources: async () => {
    const response = await fetch(`${API_BASE_URL}/resources/rep`, {
      method: 'GET',
      headers: getAuthHeaders()
    });
    return handleApiResponse(response);
  },

  /**
   * Delete a resource by ID (Admin only)
   */
  deleteResource: async (resourceId) => {
    const response = await fetch(`${API_BASE_URL}/resources/${encodeURIComponent(resourceId)}`, {
      method: 'DELETE',
      headers: getAuthHeaders()
    });
    return handleApiResponse(response);
  },

  /**
   * Stream a resource file through the authenticated API endpoint.
   * Returns a Blob for use with URL.createObjectURL.
   * @param intent - "view" (default) or "download"; recorded for engagement analytics
   */
  getResourceFile: async (resourceId: string, intent: 'view' | 'download' = 'view'): Promise<Blob> => {
    const response = await fetch(`${API_BASE_URL}/resources/${encodeURIComponent(resourceId)}/file?intent=${intent}`, {
      headers: getAuthHeaders()
    });
    if (!response.ok) throw new Error(`Failed to fetch resource file: ${response.status}`);
    return response.blob();
  },

  /**
   * Replace a resource's file (Admin only). Keeps name/tags; bumps updatedAt.
   */
  replaceResourceFile: async (resourceId: string, file: File, opts?: { announce?: boolean }) => {
    const formData = new FormData();
    formData.append('file', file);
    if (opts?.announce) formData.append('announce', 'true');

    const token = localStorage.getItem('authToken');
    const response = await fetch(`${API_BASE_URL}/resources/${encodeURIComponent(resourceId)}/replace`, {
      method: 'POST',
      headers: {
        'Authorization': token ? `Bearer ${token}` : '',
        'X-Client-Version': '2.0.0',
        'X-HIPAA-Compliant': 'true'
      },
      body: formData
    });
    return handleApiResponse(response);
  },

  /**
   * Update resource metadata (Admin only).
   * Accepts: name, visibility, categoryId (null clears), audience (null clears), sortOrder, productTagIds (replaces all)
   */
  updateResource: async (resourceId: string, data: Record<string, any>) => {
    return await apiCall('PATCH', `${API_BASE_URL}/resources/${encodeURIComponent(resourceId)}`, {
      body: JSON.stringify(data)
    });
  },

  /**
   * Bulk-update resources (Admin only). Only keys present are applied.
   */
  bulkUpdateResources: async (payload: { resourceIds: string[]; categoryId?: string | null; audience?: string | null; visibility?: string; productTagIds?: string[] }) => {
    return await apiCall('POST', `${API_BASE_URL}/resources/bulk`, {
      body: JSON.stringify(payload)
    });
  },

  /**
   * Record a SHARE engagement event (e.g. copy-link clicked)
   */
  recordResourceShare: async (resourceId: string) => {
    return await apiCall('POST', `${API_BASE_URL}/resources/${encodeURIComponent(resourceId)}/share`);
  },

  /**
   * Mint a durable PUBLIC share link for a resource. Any logged-in user who can
   * see the resource may create one; the returned URL opens the file with no
   * login required. Also records a SHARE engagement event server-side.
   */
  getResourceShareLink: async (resourceId: string): Promise<string> => {
    const resp = await apiCall('POST', `${API_BASE_URL}/resources/${encodeURIComponent(resourceId)}/share-link`);
    const slug = resp?.data?.slug;
    if (!slug) throw new Error('Failed to create share link');
    return `${getShareLinkBase()}/s/${encodeURIComponent(slug)}`;
  },

  // --- Category managed list ---
  listCategories: async () => {
    return await apiCall('GET', `${API_BASE_URL}/resources/categories`);
  },
  createCategory: async (data: { name: string; sortOrder?: number }) => {
    return await apiCall('POST', `${API_BASE_URL}/resources/categories`, { body: JSON.stringify(data) });
  },
  updateCategory: async (id: string, data: { name?: string; sortOrder?: number }) => {
    return await apiCall('PATCH', `${API_BASE_URL}/resources/categories/${encodeURIComponent(id)}`, { body: JSON.stringify(data) });
  },
  deleteCategory: async (id: string) => {
    return await apiCall('DELETE', `${API_BASE_URL}/resources/categories/${encodeURIComponent(id)}`);
  },

  // --- Product tag managed list ---
  listProductTags: async () => {
    return await apiCall('GET', `${API_BASE_URL}/resources/product-tags`);
  },
  createProductTag: async (data: { name: string; sortOrder?: number }) => {
    return await apiCall('POST', `${API_BASE_URL}/resources/product-tags`, { body: JSON.stringify(data) });
  },
  updateProductTag: async (id: string, data: { name?: string; sortOrder?: number }) => {
    return await apiCall('PATCH', `${API_BASE_URL}/resources/product-tags/${encodeURIComponent(id)}`, { body: JSON.stringify(data) });
  },
  deleteProductTag: async (id: string) => {
    return await apiCall('DELETE', `${API_BASE_URL}/resources/product-tags/${encodeURIComponent(id)}`);
  },

  // --- Engagement analytics (Admin only) ---
  getResourceAnalyticsSummary: async () => {
    return await apiCall('GET', `${API_BASE_URL}/resources/analytics/summary`);
  },
  getResourceAnalytics: async (resourceId: string) => {
    return await apiCall('GET', `${API_BASE_URL}/resources/${encodeURIComponent(resourceId)}/analytics`);
  },

  /**
   * Create (or reuse) a per-recipient TRACKED share link. The recipient enters
   * nothing; opens of the returned URL are logged per-recipient and surface in
   * the resource's analytics. Re-sharing to the same recipient (same email, or
   * same name when no email) reuses the existing link so opens accumulate.
   * Returns { url, reused }.
   */
  createTrackedShareLink: async (
    resourceId: string,
    recipient: { recipientName: string; recipientEmail?: string | null }
  ): Promise<{ url: string; reused: boolean }> => {
    const resp = await apiCall('POST', `${API_BASE_URL}/resources/${encodeURIComponent(resourceId)}/tracked-share-link`, {
      body: JSON.stringify(recipient),
    });
    const token = resp?.data?.token;
    if (!token) throw new Error('Failed to create tracked share link');
    return {
      url: buildTrackedShareUrl(token),
      reused: !!resp?.data?.reused,
    };
  },

  /** Rebuild the public tracked-share URL for a token already stored in the DB. */
  trackedShareUrl: (token: string): string => buildTrackedShareUrl(token),
};

/**
 * Calendar API - fetch EPL shipping/delivery availability
 */
export const calendarAPI = {
  /**
   * Returns valid DELIVERY dates for the next 90 days.
   * Each delivery date = EPL shipping date + 1 day (overnight shipping).
   * Respects blocked dates and configured shipping days.
   */
  getAvailableDeliveryDates: async (): Promise<string[]> => {
    const response = await fetch(`${API_BASE_URL}/calendar/available-dates?organization=EPL`, {
      headers: getAuthHeaders(),
    });
    const data = await handleApiResponse(response);
    if (!data.success) return [];
    // Shift each shipping date forward by 1 day to get the delivery date
    return (data.data.availableDates as string[]).map((dateStr: string) => {
      const d = new Date(dateStr + 'T12:00:00Z');
      d.setUTCDate(d.getUTCDate() + 1);
      return d.toISOString().split('T')[0];
    });
  },
};

export const reportsAPI = {
  getAccountingReport: async (params: { startDate?: string; endDate?: string; organization?: string } = {}) => {
    const query = new URLSearchParams();
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        query.append(key, value as string);
      }
    });
    const url = `${API_BASE_URL}/reports/accounting${query.toString() ? `?${query.toString()}` : ''}`;
    return apiCall('GET', url);
  },

  getClientSummaryReport: async (params: { startDate?: string; endDate?: string; organization?: string } = {}) => {
    const query = new URLSearchParams();
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        query.append(key, value as string);
      }
    });
    const url = `${API_BASE_URL}/reports/client-summary${query.toString() ? `?${query.toString()}` : ''}`;
    return apiCall('GET', url);
  },

  getReceivedPaymentsReport: async (params: { startDate?: string; endDate?: string; organization?: string } = {}) => {
    const query = new URLSearchParams();
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        query.append(key, value as string);
      }
    });
    const url = `${API_BASE_URL}/reports/received-payments${query.toString() ? `?${query.toString()}` : ''}`;
    return apiCall('GET', url);
  },

  getSavedReports: async (organization: string) => {
    return apiCall('GET', `${API_BASE_URL}/reports/saved?organization=${organization}`);
  },

  getSavedReport: async (id: string) => {
    return apiCall('GET', `${API_BASE_URL}/reports/saved/${id}`);
  },

  createSavedReport: async (data: any) => {
    return apiCall('POST', `${API_BASE_URL}/reports/saved`, { body: JSON.stringify(data) });
  },

  deleteSavedReport: async (id: string) => {
    return apiCall('DELETE', `${API_BASE_URL}/reports/saved/${id}`);
  },

  executeDonorReport: async (data: any) => {
    return apiCall('POST', `${API_BASE_URL}/reports/execute/donor-management`, { body: JSON.stringify(data) });
  },

  executeClientActivityReport: async (data: any) => {
    return apiCall('POST', `${API_BASE_URL}/reports/execute/client-activity`, { body: JSON.stringify(data) });
  },

  executePromoCodeReport: async (data: any) => {
    return apiCall('POST', `${API_BASE_URL}/reports/execute/promo-code-usage`, { body: JSON.stringify(data) });
  },

  getRepActivityReport: async (params: { startDate?: string; endDate?: string; organization?: string } = {}) => {
    const query = new URLSearchParams();
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        query.append(key, value as string);
      }
    });
    const url = `${API_BASE_URL}/reports/rep-activity${query.toString() ? `?${query.toString()}` : ''}`;
    return apiCall('GET', url);
  },
};

export const shipstationAPI = {
  /**
   * Create shipping label for an order
   */
  createLabel: async (orderId, carrier, service, shipDate) => {
    const response = await fetch(`${API_BASE_URL}/orders/${orderId}/create-label`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify({ carrier, service, shipDate })
    });
    return handleApiResponse(response);
  },

  /**
   * Get tracking status for a shipment
   */
  getTrackingStatus: async (trackingNumber) => {
    const response = await fetch(`${API_BASE_URL}/shipstation/tracking/${trackingNumber}`, {
      method: 'GET',
      headers: getAuthHeaders()
    });
    return handleApiResponse(response);
  },

  /**
   * Get shipping rates for an order
   * @param {string} orderId - The order ID
   * @param {Object} options - Optional package details to use for rate calculation
   * @param {number} options.packageWeightLbs - Package weight (product + packing, no dry ice)
   * @param {number} options.dryIceWeightLbs - Dry ice weight (tracked separately)
   * @param {string} options.packageSize - Package size code (xs_box, s_box, m_box, l_box)
   */
  getRates: async (orderId, options = {}) => {
    const params = new URLSearchParams();
    
    if (options.packageWeightLbs && options.packageWeightLbs > 0) {
      params.append('packageWeightLbs', options.packageWeightLbs.toString());
    }
    if (options.dryIceWeightLbs && options.dryIceWeightLbs > 0) {
      params.append('dryIceWeightLbs', options.dryIceWeightLbs.toString());
    }
    if (options.packageSize) {
      params.append('packageSize', options.packageSize);
    }
    
    const queryString = params.toString();
    const url = `${API_BASE_URL}/shipstation/rates/${orderId}${queryString ? `?${queryString}` : ''}`;
    
    const response = await fetch(url, {
      method: 'GET',
      headers: getAuthHeaders()
    });
    return handleApiResponse(response);
  },

  createLabelForOrder: (orderId, carrierCode, serviceCode, shipDate, options = {}) => {
    logger.log('--- Step 2: shipstationAPI.createLabelForOrder ---');
    const url = `${API_BASE_URL}/shipstation/orders/${orderId}/create-label`;
    logger.log('Calling API URL:', url);
    const payload = {
      carrierCode,
      serviceCode,
      shipDate
    };

    if (options.weight) {
      payload.weight = options.weight;
    }

    if (options.dimensions) {
      payload.dimensions = options.dimensions;
    }

    // Dry ice weight tracked separately for regulatory compliance
    if (options.dryIceWeightLbs && options.dryIceWeightLbs > 0) {
      payload.dryIceWeightLbs = options.dryIceWeightLbs;
    }

    return apiCall('POST', url, {
      body: JSON.stringify(payload)
    });
  },
  /**
   * Manually create a ShipStation order for an existing order that wasn't created during checkout.
   */
  createShipStationOrder: async (orderId) => {
    const url = `${API_BASE_URL}/orders/${orderId}/create-shipstation-order`;
    return apiCall('POST', url);
  },

  // getTrackingStatus was duplicated and is correctly in shipstationAPI
};

// ===================================
// CLIENT DASHBOARD API
// ===================================

export const clientDashboardAPI = {
  getDashboardData: async () => {
    const params = new URLSearchParams();
    const perspectiveClientId = getPerspectiveClientId();
    if (perspectiveClientId) {
      params.append('clientId', perspectiveClientId);
    }
    const previewParentId = getAdminParentPreviewId();
    if (previewParentId) {
      params.append('adminPreviewParentCompanyId', previewParentId);
    }
    const qs = params.toString();
    return await apiCall('GET', `${API_BASE_URL}/client-dashboard${qs ? `?${qs}` : ''}`);
  },
};

// ===================================
// CLIENT INVENTORY TRACKING API
// ===================================

export const clientInventoryAPI = {
  /**
   * Get all inventory items for the logged-in customer
   * Automatically includes clientId when admin is viewing as client
   */
  getInventory: async ({ status = 'ALL', consignmentOnly, page = 1, limit = 50, orderNumber, search, clientId }: { status?: string; consignmentOnly?: boolean; page?: number; limit?: number; orderNumber?: string; search?: string; clientId?: string } = {}) => {
    const params = new URLSearchParams({
      status,
      page: page.toString(),
      limit: limit.toString()
    });

    if (consignmentOnly) {
      params.append('consignmentOnly', 'true');
    }

    if (search) {
      params.append('search', search);
    } else if (orderNumber) {
      params.append('orderNumber', orderNumber);
    }

    // Add clientId when explicitly provided or viewing as client (admin perspective)
    if (clientId) {
      params.append('clientId', clientId);
    } else {
      const perspectiveClientId = getPerspectiveClientId();
      if (perspectiveClientId) {
        params.append('clientId', perspectiveClientId);
      }
    }
    const previewParentId = getAdminParentPreviewId();
    if (previewParentId) {
      params.append('adminPreviewParentCompanyId', previewParentId);
    }
    
    const response = await fetch(`${API_BASE_URL}/client-inventory?${params}`, {
      method: 'GET',
      headers: getAuthHeaders(),
      credentials: 'include'
    });
    
    return await handleApiResponse(response, {
      url: `${API_BASE_URL}/client-inventory?${params}`,
      method: 'GET',
      headers: getAuthHeaders()
    });
  },
  
  /**
   * Get detailed information for a specific tissue ID
   * Automatically includes clientId when admin is viewing as client
   */
  getDetails: async (tissueId) => {
    const params = new URLSearchParams();
    
    // Add clientId when viewing as client (admin perspective)
    const perspectiveClientId = getPerspectiveClientId();
    if (perspectiveClientId) {
      params.append('clientId', perspectiveClientId);
    }
    const previewParentId = getAdminParentPreviewId();
    if (previewParentId) {
      params.append('adminPreviewParentCompanyId', previewParentId);
    }
    
    const queryString = params.toString();
    const url = `${API_BASE_URL}/client-inventory/${tissueId}${queryString ? `?${queryString}` : ''}`;
    
    const response = await fetch(url, {
      method: 'GET',
      headers: getAuthHeaders(),
      credentials: 'include'
    });
    
    return await handleApiResponse(response, {
      url,
      method: 'GET',
      headers: getAuthHeaders()
    });
  },
  
  /**
   * Mark a tissue ID as USED
   * Automatically includes clientId when admin is viewing as client
   */
  markUsed: async (tissueId, data) => {
    // Add clientId when viewing as client (admin perspective)
    const perspectiveClientId = getPerspectiveClientId();
    const bodyData = perspectiveClientId 
      ? { ...data, clientId: perspectiveClientId }
      : data;
    
    const response = await fetch(`${API_BASE_URL}/client-inventory/${tissueId}/mark-used`, {
      method: 'POST',
      headers: getAuthHeaders(),
      credentials: 'include',
      body: JSON.stringify(bodyData)
    });
    
    return await handleApiResponse(response, {
      url: `${API_BASE_URL}/client-inventory/${tissueId}/mark-used`,
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify(bodyData)
    });
  },
  
  /**
   * Mark a tissue ID as DISCARDED
   * Automatically includes clientId when admin is viewing as client
   */
  markDiscarded: async (tissueId, data) => {
    // Add clientId when viewing as client (admin perspective)
    const perspectiveClientId = getPerspectiveClientId();
    const bodyData = perspectiveClientId 
      ? { ...data, clientId: perspectiveClientId }
      : data;
    
    const response = await fetch(`${API_BASE_URL}/client-inventory/${tissueId}/mark-discarded`, {
      method: 'POST',
      headers: getAuthHeaders(),
      credentials: 'include',
      body: JSON.stringify(bodyData)
    });
    
    return await handleApiResponse(response, {
      url: `${API_BASE_URL}/client-inventory/${tissueId}/mark-discarded`,
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify(bodyData)
    });
  },
  
  /**
   * Update existing tracking information
   * Automatically includes clientId when admin is viewing as client
   */
  updateTracking: async (tissueId, data) => {
    // Add clientId when viewing as client (admin perspective)
    const perspectiveClientId = getPerspectiveClientId();
    const bodyData = perspectiveClientId 
      ? { ...data, clientId: perspectiveClientId }
      : data;
    
    const response = await fetch(`${API_BASE_URL}/client-inventory/${tissueId}`, {
      method: 'PUT',
      headers: getAuthHeaders(),
      credentials: 'include',
      body: JSON.stringify(bodyData)
    });
    
    return await handleApiResponse(response, {
      url: `${API_BASE_URL}/client-inventory/${tissueId}`,
      method: 'PUT',
      headers: getAuthHeaders(),
      body: JSON.stringify(bodyData)
    });
  },
  
  /**
   * Get summary statistics
   * Automatically includes clientId when admin is viewing as client
   */
  getStats: async () => {
    const params = new URLSearchParams();
    
    // Add clientId when viewing as client (admin perspective)
    const perspectiveClientId = getPerspectiveClientId();
    if (perspectiveClientId) {
      params.append('clientId', perspectiveClientId);
    }
    
    const queryString = params.toString();
    const url = `${API_BASE_URL}/client-inventory/stats${queryString ? `?${queryString}` : ''}`;
    
    const response = await fetch(url, {
      method: 'GET',
      headers: getAuthHeaders(),
      credentials: 'include'
    });
    
    return await handleApiResponse(response, {
      url,
      method: 'GET',
      headers: getAuthHeaders()
    });
  },

  // Admin endpoints
  admin: {
    /**
     * Get all client inventory (admin only)
     */
    getAllInventory: async ({ userId, status = 'ALL', page = 1, limit = 50 } = {}) => {
      const params = new URLSearchParams({
        status,
        page: page.toString(),
        limit: limit.toString()
      });
      
      if (userId) {
        params.append('userId', userId);
      }
      
      const response = await fetch(`${API_BASE_URL}/client-inventory/admin/all?${params}`, {
        method: 'GET',
        headers: getAuthHeaders(),
        credentials: 'include'
      });
      
      return await handleApiResponse(response, {
        url: `${API_BASE_URL}/client-inventory/admin/all?${params}`,
        method: 'GET',
        headers: getAuthHeaders()
      });
    },

    /**
     * Get aggregate stats (admin only)
     */
    getStats: async (userId) => {
      const params = userId ? `?userId=${userId}` : '';
      const response = await fetch(`${API_BASE_URL}/client-inventory/admin/stats${params}`, {
        method: 'GET',
        headers: getAuthHeaders(),
        credentials: 'include'
      });
      
      return await handleApiResponse(response, {
        url: `${API_BASE_URL}/client-inventory/admin/stats${params}`,
        method: 'GET',
        headers: getAuthHeaders()
      });
    }
  }
};

// ===================================
// PROMO CODE API
// ===================================

export const promoCodeAPI = {
  /**
   * Get all active promo codes available for reps to select from
   */
  getAvailablePromoCodes: async () => {
    const response = await fetch(`${API_BASE_URL}/promo-codes/rep/available`, {
      method: 'GET',
      headers: getAuthHeaders(),
      credentials: 'include'
    });

    return await handleApiResponse(response, {
      url: `${API_BASE_URL}/promo-codes/rep/available`,
      method: 'GET',
      headers: getAuthHeaders()
    });
  },

  /**
   * Get all promo codes (admin only)
   */
  getAllPromoCodes: async ({ page = 1, limit = 20, organization = 'ALL', status = 'ALL', search = '' } = {}) => {
    const params = new URLSearchParams({
      page: page.toString(),
      limit: limit.toString(),
      organization,
      status
    });
    
    if (search) {
      params.append('search', search);
    }
    
    const response = await fetch(`${API_BASE_URL}/promo-codes?${params}`, {
      method: 'GET',
      headers: getAuthHeaders(),
      credentials: 'include'
    });
    
    return await handleApiResponse(response, {
      url: `${API_BASE_URL}/promo-codes?${params}`,
      method: 'GET',
      headers: getAuthHeaders()
    });
  },
  
  /**
   * Get promo code details
   */
  getPromoCode: async (id) => {
    const response = await fetch(`${API_BASE_URL}/promo-codes/${id}`, {
      method: 'GET',
      headers: getAuthHeaders(),
      credentials: 'include'
    });
    
    return await handleApiResponse(response, {
      url: `${API_BASE_URL}/promo-codes/${id}`,
      method: 'GET',
      headers: getAuthHeaders()
    });
  },
  
  /**
   * Create new promo code
   */
  createPromoCode: async (data) => {
    const response = await fetch(`${API_BASE_URL}/promo-codes`, {
      method: 'POST',
      headers: getAuthHeaders(),
      credentials: 'include',
      body: JSON.stringify(data)
    });
    
    return await handleApiResponse(response, {
      url: `${API_BASE_URL}/promo-codes`,
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify(data)
    });
  },
  
  /**
   * Update promo code
   */
  updatePromoCode: async (id, data) => {
    const response = await fetch(`${API_BASE_URL}/promo-codes/${id}`, {
      method: 'PUT',
      headers: getAuthHeaders(),
      credentials: 'include',
      body: JSON.stringify(data)
    });
    
    return await handleApiResponse(response, {
      url: `${API_BASE_URL}/promo-codes/${id}`,
      method: 'PUT',
      headers: getAuthHeaders(),
      body: JSON.stringify(data)
    });
  },
  
  /**
   * Delete/deactivate promo code
   */
  deletePromoCode: async (id) => {
    const response = await fetch(`${API_BASE_URL}/promo-codes/${id}`, {
      method: 'DELETE',
      headers: getAuthHeaders(),
      credentials: 'include'
    });
    
    return await handleApiResponse(response, {
      url: `${API_BASE_URL}/promo-codes/${id}`,
      method: 'DELETE',
      headers: getAuthHeaders()
    });
  },
  
  /**
   * Get promo code usage history
   */
  getPromoCodeUsage: async (id, { page = 1, limit = 20 } = {}) => {
    const params = new URLSearchParams({
      page: page.toString(),
      limit: limit.toString()
    });
    
    const response = await fetch(`${API_BASE_URL}/promo-codes/${id}/usage?${params}`, {
      method: 'GET',
      headers: getAuthHeaders(),
      credentials: 'include'
    });
    
    return await handleApiResponse(response, {
      url: `${API_BASE_URL}/promo-codes/${id}/usage?${params}`,
      method: 'GET',
      headers: getAuthHeaders()
    });
  },
  
  /**
   * Validate promo code (client)
   */
  validatePromoCode: async (data) => {
    const response = await fetch(`${API_BASE_URL}/promo-codes/validate`, {
      method: 'POST',
      headers: getAuthHeaders(),
      credentials: 'include',
      body: JSON.stringify(data)
    });
    
    return await handleApiResponse(response, {
      url: `${API_BASE_URL}/promo-codes/validate`,
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify(data)
    });
  },
  
  /**
   * Apply promo code to order (client)
   */
  applyPromoCode: async (data) => {
    const response = await fetch(`${API_BASE_URL}/promo-codes/apply`, {
      method: 'POST',
      headers: getAuthHeaders(),
      credentials: 'include',
      body: JSON.stringify(data)
    });
    
    return await handleApiResponse(response, {
      url: `${API_BASE_URL}/promo-codes/apply`,
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify(data)
    });
  },
  
  /**
   * Get my promo code usage (client)
   */
  getMyPromoUsage: async () => {
    const response = await fetch(`${API_BASE_URL}/promo-codes/my-usage`, {
      method: 'GET',
      headers: getAuthHeaders(),
      credentials: 'include'
    });
    
    return await handleApiResponse(response, {
      url: `${API_BASE_URL}/promo-codes/my-usage`,
      method: 'GET',
      headers: getAuthHeaders()
    });
  }
};

// =====================================================
// REP SYSTEM API
// =====================================================
export const repAPI = {
  /**
   * Get all reps (Admin only)
   */
  getReps: async () => {
    const response = await fetch(`${API_BASE_URL}/reps`, {
      method: 'GET',
      headers: getAuthHeaders(),
      credentials: 'include'
    });
    
    return await handleApiResponse(response, {
      url: `${API_BASE_URL}/reps`,
      method: 'GET',
      headers: getAuthHeaders()
    });
  },
  
  /**
   * Create new rep (Admin only)
   */
  createRep: async (repData) => {
    const response = await fetch(`${API_BASE_URL}/reps`, {
      method: 'POST',
      headers: getAuthHeaders(),
      credentials: 'include',
      body: JSON.stringify(repData)
    });
    
    return await handleApiResponse(response, {
      url: `${API_BASE_URL}/reps`,
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify(repData)
    });
  },
  
  /**
   * Update rep (Admin only)
   */
  updateRep: async (repId, repData) => {
    const response = await fetch(`${API_BASE_URL}/reps/${repId}`, {
      method: 'PUT',
      headers: getAuthHeaders(),
      credentials: 'include',
      body: JSON.stringify(repData)
    });
    
    return await handleApiResponse(response, {
      url: `${API_BASE_URL}/reps/${repId}`,
      method: 'PUT',
      headers: getAuthHeaders(),
      body: JSON.stringify(repData)
    });
  },
  
  /**
   * Delete rep (Admin only)
   */
  deleteRep: async (repId) => {
    const response = await fetch(`${API_BASE_URL}/reps/${repId}`, {
      method: 'DELETE',
      headers: getAuthHeaders(),
      credentials: 'include'
    });
    
    return await handleApiResponse(response, {
      url: `${API_BASE_URL}/reps/${repId}`,
      method: 'DELETE',
      headers: getAuthHeaders()
    });
  },
  
  /**
   * Get rep's clients
   */
  getRepClients: async (repId) => {
    const response = await fetch(`${API_BASE_URL}/reps/${repId}/clients`, {
      method: 'GET',
      headers: getAuthHeaders(),
      credentials: 'include'
    });
    
    return await handleApiResponse(response, {
      url: `${API_BASE_URL}/reps/${repId}/clients`,
      method: 'GET',
      headers: getAuthHeaders()
    });
  },
  
  /**
   * Assign client to rep
   */
  assignClient: async (data) => {
    const response = await fetch(`${API_BASE_URL}/reps/assign-client`, {
      method: 'POST',
      headers: getAuthHeaders(),
      credentials: 'include',
      body: JSON.stringify(data)
    });
    
    return await handleApiResponse(response, {
      url: `${API_BASE_URL}/reps/assign-client`,
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify(data)
    });
  },

  /**
   * Get client's current rep assignment
   */
  getClientRep: async (clientId) => {
    const response = await fetch(`${API_BASE_URL}/reps/client/${clientId}`, {
      method: 'GET',
      headers: getAuthHeaders(),
      credentials: 'include'
    });
    
    return await handleApiResponse(response, {
      url: `${API_BASE_URL}/reps/client/${clientId}`,
      method: 'GET',
      headers: getAuthHeaders()
    });
  },

  /**
   * Reassign client to different rep
   */
  reassignClient: async (clientId, data) => {
    const response = await fetch(`${API_BASE_URL}/reps/reassign-client/${clientId}`, {
      method: 'PUT',
      headers: getAuthHeaders(),
      credentials: 'include',
      body: JSON.stringify(data)
    });
    
    return await handleApiResponse(response, {
      url: `${API_BASE_URL}/reps/reassign-client/${clientId}`,
      method: 'PUT',
      headers: getAuthHeaders(),
      body: JSON.stringify(data)
    });
  },
  
  /**
   * Get all leads
   */
  getLeads: async (params) => {
    const queryString = new URLSearchParams(params).toString();
    const url = `${API_BASE_URL}/leads${queryString ? '?' + queryString : ''}`;
    
    const response = await fetch(url, {
      method: 'GET',
      headers: getAuthHeaders(),
      credentials: 'include'
    });
    
    return await handleApiResponse(response, {
      url,
      method: 'GET',
      headers: getAuthHeaders()
    });
  },

  /** Admin-only marketing cleanup export source; filtering is enforced server-side. */
  getMarketingLeadExport: async () => {
    const url = `${API_BASE_URL}/leads/marketing-export`;
    const response = await fetch(url, {
      method: 'GET',
      headers: getAuthHeaders(),
      credentials: 'include'
    });

    return await handleApiResponse(response, {
      url,
      method: 'GET',
      headers: getAuthHeaders()
    });
  },

  /**
   * Reps available for lead assignment, each with region (repShippingState +
   * territory) and current OPEN lead count. Internal only.
   */
  getAssignableReps: async () => {
    const url = `${API_BASE_URL}/leads/assignable-reps`;
    const response = await fetch(url, { method: 'GET', headers: getAuthHeaders(), credentials: 'include' });
    return await handleApiResponse(response, { url, method: 'GET', headers: getAuthHeaders() });
  },

  /**
   * Bulk-assign leads to a rep (repId), or unassign (repId = null). Internal only.
   * @param {{ leadIds: string[], repId: string | null }} payload
   */
  assignLeads: async (payload) => {
    const url = `${API_BASE_URL}/leads/assign`;
    const response = await fetch(url, {
      method: 'POST', headers: getAuthHeaders(), credentials: 'include',
      body: JSON.stringify(payload),
    });
    return await handleApiResponse(response, { url, method: 'POST', headers: getAuthHeaders() });
  },

  /**
   * Get recent lead activities across all leads for the org (CRM dashboard feed)
   */
  getRecentActivities: async ({ organization, limit = 50 } = {}) => {
    const query = new URLSearchParams();
    if (organization) query.append('organization', organization);
    if (limit) query.append('limit', String(limit));
    const url = `${API_BASE_URL}/leads/activities/recent?${query.toString()}`;
    const response = await fetch(url, {
      method: 'GET',
      headers: getAuthHeaders(),
      credentials: 'include',
    });
    return await handleApiResponse(response, { url, method: 'GET', headers: getAuthHeaders() });
  },

  /**
   * Create lead
   */
  createLead: async (leadData) => {
    const response = await fetch(`${API_BASE_URL}/leads`, {
      method: 'POST',
      headers: getAuthHeaders(),
      credentials: 'include',
      body: JSON.stringify(leadData)
    });
    
    return await handleApiResponse(response, {
      url: `${API_BASE_URL}/leads`,
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify(leadData)
    });
  },
  
  /**
   * Get single lead by ID
   */
  getLead: async (leadId) => {
    const response = await fetch(`${API_BASE_URL}/leads/${leadId}`, {
      method: 'GET',
      headers: getAuthHeaders(),
      credentials: 'include'
    });
    
    return await handleApiResponse(response, {
      url: `${API_BASE_URL}/leads/${leadId}`,
      method: 'GET',
      headers: getAuthHeaders()
    });
  },

  /**
   * Update lead
   */
  updateLead: async (leadId, leadData) => {
    const response = await fetch(`${API_BASE_URL}/leads/${leadId}`, {
      method: 'PUT',
      headers: getAuthHeaders(),
      credentials: 'include',
      body: JSON.stringify(leadData)
    });
    
    return await handleApiResponse(response, {
      url: `${API_BASE_URL}/leads/${leadId}`,
      method: 'PUT',
      headers: getAuthHeaders(),
      body: JSON.stringify(leadData)
    });
  },

  /** Set a vet's license validation state (Flow 5). validationStatus: PENDING |
   *  NEEDS_INFO | MANUAL_REVIEW | VALIDATED | REJECTED. */
  validateLead: async (leadId, validationStatus, notes) => {
    const url = `${API_BASE_URL}/leads/${leadId}/validate`;
    const body = JSON.stringify({ validationStatus, notes });
    const response = await fetch(url, { method: 'POST', headers: getAuthHeaders(), credentials: 'include', body });
    return await handleApiResponse(response, { url, method: 'POST', headers: getAuthHeaders(), body });
  },

  /**
   * Push lead to admin
   */
  pushLeadToAdmin: async (leadId) => {
    const response = await fetch(`${API_BASE_URL}/leads/${leadId}/push-to-admin`, {
      method: 'POST',
      headers: getAuthHeaders(),
      credentials: 'include'
    });
    
    return await handleApiResponse(response, {
      url: `${API_BASE_URL}/leads/${leadId}/push-to-admin`,
      method: 'POST',
      headers: getAuthHeaders()
    });
  },

  /**
   * Add note to lead
   */
  addLeadNote: async (leadId, content, isInternal = false) => {
    const response = await fetch(`${API_BASE_URL}/leads/${leadId}/notes`, {
      method: 'POST',
      headers: getAuthHeaders(),
      credentials: 'include',
      body: JSON.stringify({ content, isInternal })
    });
    
    return await handleApiResponse(response, {
      url: `${API_BASE_URL}/leads/${leadId}/notes`,
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify({ content, isInternal })
    });
  },

  /**
   * Update note
   */
  updateLeadNote: async (leadId, noteId, content, isInternal) => {
    const response = await fetch(`${API_BASE_URL}/leads/${leadId}/notes/${noteId}`, {
      method: 'PUT',
      headers: getAuthHeaders(),
      credentials: 'include',
      body: JSON.stringify({ content, isInternal })
    });
    
    return await handleApiResponse(response, {
      url: `${API_BASE_URL}/leads/${leadId}/notes/${noteId}`,
      method: 'PUT',
      headers: getAuthHeaders(),
      body: JSON.stringify({ content, isInternal })
    });
  },

  /**
   * Delete note
   */
  deleteLeadNote: async (leadId, noteId) => {
    const response = await fetch(`${API_BASE_URL}/leads/${leadId}/notes/${noteId}`, {
      method: 'DELETE',
      headers: getAuthHeaders(),
      credentials: 'include'
    });
    
    return await handleApiResponse(response, {
      url: `${API_BASE_URL}/leads/${leadId}/notes/${noteId}`,
      method: 'DELETE',
      headers: getAuthHeaders()
    });
  },

  /**
   * Log activity for lead (CALL, EMAIL, MEETING, etc.)
   */
  logLeadActivity: async (leadId, activityType, description, metadata = null, activityDate = null) => {
    const response = await fetch(`${API_BASE_URL}/leads/${leadId}/activities`, {
      method: 'POST',
      headers: getAuthHeaders(),
      credentials: 'include',
      body: JSON.stringify({ activityType, description, metadata, activityDate })
    });

    return await handleApiResponse(response, {
      url: `${API_BASE_URL}/leads/${leadId}/activities`,
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify({ activityType, description, metadata, activityDate })
    });
  },

  /**
   * Update activity
   */
  updateLeadActivity: async (leadId, activityId, activityType, description, metadata = null, activityDate = null) => {
    const response = await fetch(`${API_BASE_URL}/leads/${leadId}/activities/${activityId}`, {
      method: 'PUT',
      headers: getAuthHeaders(),
      credentials: 'include',
      body: JSON.stringify({ activityType, description, metadata, activityDate })
    });

    return await handleApiResponse(response, {
      url: `${API_BASE_URL}/leads/${leadId}/activities/${activityId}`,
      method: 'PUT',
      headers: getAuthHeaders(),
      body: JSON.stringify({ activityType, description, metadata, activityDate })
    });
  },

  /**
   * Delete activity
   */
  deleteLeadActivity: async (leadId, activityId) => {
    const response = await fetch(`${API_BASE_URL}/leads/${leadId}/activities/${activityId}`, {
      method: 'DELETE',
      headers: getAuthHeaders(),
      credentials: 'include'
    });
    
    return await handleApiResponse(response, {
      url: `${API_BASE_URL}/leads/${leadId}/activities/${activityId}`,
      method: 'DELETE',
      headers: getAuthHeaders()
    });
  },

  /**
   * Add contact to lead
   */
  addLeadContact: async (leadId, contactData) => {
    const response = await fetch(`${API_BASE_URL}/leads/${leadId}/contacts`, {
      method: 'POST',
      headers: getAuthHeaders(),
      credentials: 'include',
      body: JSON.stringify(contactData)
    });
    
    return await handleApiResponse(response, {
      url: `${API_BASE_URL}/leads/${leadId}/contacts`,
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify(contactData)
    });
  },

  /**
   * Update contact
   */
  updateLeadContact: async (leadId, contactId, contactData) => {
    const response = await fetch(`${API_BASE_URL}/leads/${leadId}/contacts/${contactId}`, {
      method: 'PUT',
      headers: getAuthHeaders(),
      credentials: 'include',
      body: JSON.stringify(contactData)
    });
    
    return await handleApiResponse(response, {
      url: `${API_BASE_URL}/leads/${leadId}/contacts/${contactId}`,
      method: 'PUT',
      headers: getAuthHeaders(),
      body: JSON.stringify(contactData)
    });
  },

  /**
   * Delete contact
   */
  deleteLeadContact: async (leadId, contactId) => {
    const response = await fetch(`${API_BASE_URL}/leads/${leadId}/contacts/${contactId}`, {
      method: 'DELETE',
      headers: getAuthHeaders(),
      credentials: 'include'
    });
    
    return await handleApiResponse(response, {
      url: `${API_BASE_URL}/leads/${leadId}/contacts/${contactId}`,
      method: 'DELETE',
      headers: getAuthHeaders()
    });
  },

  /**
   * Request login for contact
   */
  requestLeadLogin: async (leadId, loginData) => {
    const response = await fetch(`${API_BASE_URL}/leads/${leadId}/login-requests`, {
      method: 'POST',
      headers: getAuthHeaders(),
      credentials: 'include',
      body: JSON.stringify(loginData)
    });
    
    return await handleApiResponse(response, {
      url: `${API_BASE_URL}/leads/${leadId}/login-requests`,
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify(loginData)
    });
  },

  /**
   * Get login requests for lead
   */
  getLeadLoginRequests: async (leadId) => {
    const response = await fetch(`${API_BASE_URL}/leads/${leadId}/login-requests`, {
      method: 'GET',
      headers: getAuthHeaders(),
      credentials: 'include'
    });
    
    return await handleApiResponse(response, {
      url: `${API_BASE_URL}/leads/${leadId}/login-requests`,
      method: 'GET',
      headers: getAuthHeaders()
    });
  },

  /**
   * Delete login request
   */
  deleteLeadLoginRequest: async (leadId, requestId) => {
    const response = await fetch(`${API_BASE_URL}/leads/${leadId}/login-requests/${requestId}`, {
      method: 'DELETE',
      headers: getAuthHeaders(),
      credentials: 'include'
    });
    
    return await handleApiResponse(response, {
      url: `${API_BASE_URL}/leads/${leadId}/login-requests/${requestId}`,
      method: 'DELETE',
      headers: getAuthHeaders()
    });
  },

  /**
   * Approve login request (Admin only)
   */
  approveLeadLoginRequest: async (leadId, requestId) => {
    const response = await fetch(`${API_BASE_URL}/leads/${leadId}/login-requests/${requestId}/approve`, {
      method: 'POST',
      headers: getAuthHeaders(),
      credentials: 'include'
    });
    
    return await handleApiResponse(response, {
      url: `${API_BASE_URL}/leads/${leadId}/login-requests/${requestId}/approve`,
      method: 'POST',
      headers: getAuthHeaders()
    });
  },

  /**
   * Reject login request (Admin only)
   */
  rejectLeadLoginRequest: async (leadId, requestId, reason) => {
    const response = await fetch(`${API_BASE_URL}/leads/${leadId}/login-requests/${requestId}/reject`, {
      method: 'POST',
      headers: getAuthHeaders(),
      credentials: 'include',
      body: JSON.stringify({ reason })
    });
    
    return await handleApiResponse(response, {
      url: `${API_BASE_URL}/leads/${leadId}/login-requests/${requestId}/reject`,
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify({ reason })
    });
  },
  
  /**
   * Get rep's orders
   */
  getRepOrders: async () => {
    const response = await fetch(`${API_BASE_URL}/orders/rep/my-orders`, {
      method: 'GET',
      headers: getAuthHeaders(),
      credentials: 'include'
    });
    
    return await handleApiResponse(response, {
      url: `${API_BASE_URL}/orders/rep/my-orders`,
      method: 'GET',
      headers: getAuthHeaders()
    });
  },
  
  /**
   * Get rep analytics dashboard
   */
  getRepAnalytics: async (params = {}) => {
    const query = new URLSearchParams();
    if (params.startDate) query.set('startDate', params.startDate);
    if (params.endDate) query.set('endDate', params.endDate);
    const qs = query.toString();
    const url = `${API_BASE_URL}/rep-analytics/dashboard${qs ? `?${qs}` : ''}`;
    const response = await fetch(url, {
      method: 'GET',
      headers: getAuthHeaders(),
      credentials: 'include'
    });

    return await handleApiResponse(response, {
      url,
      method: 'GET',
      headers: getAuthHeaders()
    });
  },

  /**
   * Get rep client breakdown
   */
  getRepClientBreakdown: async (params = {}) => {
    const query = new URLSearchParams();
    if (params.startDate) query.set('startDate', params.startDate);
    if (params.endDate) query.set('endDate', params.endDate);
    const qs = query.toString();
    const url = `${API_BASE_URL}/rep-analytics/client-breakdown${qs ? `?${qs}` : ''}`;
    const response = await fetch(url, {
      method: 'GET',
      headers: getAuthHeaders(),
      credentials: 'include'
    });
    
    return await handleApiResponse(response, {
      url: `${API_BASE_URL}/rep-analytics/client-breakdown`,
      method: 'GET',
      headers: getAuthHeaders()
    });
  },
  
  /**
   * Get rep sales chart data (monthly/quarterly)
   */
  getRepSalesCharts: async () => {
    return apiCall('GET', `${API_BASE_URL}/rep-analytics/sales-charts`);
  },

  /**
   * Get rep inventory
   */
  getRepInventory: async () => {
    const response = await fetch(`${API_BASE_URL}/rep-inventory/my-stock`, {
      method: 'GET',
      headers: getAuthHeaders(),
      credentials: 'include'
    });
    
    return await handleApiResponse(response, {
      url: `${API_BASE_URL}/rep-inventory/my-stock`,
      method: 'GET',
      headers: getAuthHeaders()
    });
  },

  /**
   * Get rep's own inventory (at their location)
   */
  getMyOwnInventory: async () => {
    const response = await fetch(`${API_BASE_URL}/rep-inventory/my-own-stock`, {
      method: 'GET',
      headers: getAuthHeaders(),
      credentials: 'include'
    });
    
    return await handleApiResponse(response, {
      url: `${API_BASE_URL}/rep-inventory/my-own-stock`,
      method: 'GET',
      headers: getAuthHeaders()
    });
  },

  getSkusForBatch: async (inventoryId) => {
    const response = await fetch(`${API_BASE_URL}/rep-inventory/batch-skus/${inventoryId}`, {
      method: 'GET',
      headers: getAuthHeaders(),
      credentials: 'include'
    });

    return await handleApiResponse(response, {
      url: `${API_BASE_URL}/rep-inventory/batch-skus/${inventoryId}`,
      method: 'GET',
      headers: getAuthHeaders()
    });
  },

  /**
   * Assign inventory to rep (Admin only)
   */
  assignInventoryToRep: async (repId, inventoryIds) => {
    const response = await fetch(`${API_BASE_URL}/rep-inventory/assign-to-rep`, {
      method: 'POST',
      headers: getAuthHeaders(),
      credentials: 'include',
      body: JSON.stringify({ repId, inventoryIds })
    });
    
    return await handleApiResponse(response, {
      url: `${API_BASE_URL}/rep-inventory/assign-to-rep`,
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify({ repId, inventoryIds })
    });
  },

  /**
   * Get rep's clients
   */
  getMyClients: async () => {
    const response = await fetch(`${API_BASE_URL}/reps/my-clients`, {
      method: 'GET',
      headers: getAuthHeaders(),
      credentials: 'include'
    });
    
    return await handleApiResponse(response, {
      url: `${API_BASE_URL}/reps/my-clients`,
      method: 'GET',
      headers: getAuthHeaders()
    });
  },

  /**
   * Get single client details (for rep client detail/edit). Use when rep is viewing one of their clients.
   */
  getMyClient: async (clientId) => {
    const response = await fetch(`${API_BASE_URL}/reps/my-clients/${clientId}?t=${Date.now()}`, {
      method: 'GET',
      headers: getAuthHeaders(),
      credentials: 'include'
    });
    return await handleApiResponse(response);
  },

  /**
   * Update client (for rep client edit). Rep can only update their assigned clients.
   */
  updateMyClient: async (clientId, payload) => {
    const response = await fetch(`${API_BASE_URL}/reps/my-clients/${clientId}`, {
      method: 'PATCH',
      headers: getAuthHeaders(),
      credentials: 'include',
      body: JSON.stringify(payload)
    });
    return await handleApiResponse(response);
  },

  /**
   * Update client inventory status (used/discarded)
   */
  updateClientInventory: async (trackingId, data) => {
    const response = await fetch(`${API_BASE_URL}/rep-inventory/client-inventory/${trackingId}/update`, {
      method: 'PUT',
      headers: getAuthHeaders(),
      credentials: 'include',
      body: JSON.stringify(data)
    });
    
    return await handleApiResponse(response, {
      url: `${API_BASE_URL}/rep-inventory/client-inventory/${trackingId}/update`,
      method: 'PUT',
      headers: getAuthHeaders(),
      body: JSON.stringify(data)
    });
  },

  /**
   * Get rep's promo codes
   */
  getMyPromoCodes: async () => {
    const response = await fetch(`${API_BASE_URL}/promo-codes/rep/my-promos`, {
      method: 'GET',
      headers: getAuthHeaders(),
      credentials: 'include'
    });
    
    return await handleApiResponse(response, {
      url: `${API_BASE_URL}/promo-codes/rep/my-promos`,
      method: 'GET',
      headers: getAuthHeaders()
    });
  },

  /**
   * Create promo code for clients
   */
  createPromoCode: async (data) => {
    const response = await fetch(`${API_BASE_URL}/promo-codes/rep/create`, {
      method: 'POST',
      headers: getAuthHeaders(),
      credentials: 'include',
      body: JSON.stringify(data)
    });
    
    return await handleApiResponse(response, {
      url: `${API_BASE_URL}/promo-codes/rep/create`,
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify(data)
    });
  },

  /**
   * Update promo code
   */
  updatePromoCode: async (promoId, data) => {
    const response = await fetch(`${API_BASE_URL}/promo-codes/rep/${promoId}`, {
      method: 'PUT',
      headers: getAuthHeaders(),
      credentials: 'include',
      body: JSON.stringify(data)
    });
    
    return await handleApiResponse(response, {
      url: `${API_BASE_URL}/promo-codes/rep/${promoId}`,
      method: 'PUT',
      headers: getAuthHeaders(),
      body: JSON.stringify(data)
    });
  }
};

// =====================================================
// RAW MATERIALS API
// =====================================================
export const rawMaterialsAPI = {
  // Items (Master List)
  getItems: async (organization) => {
    return await apiCall('GET', `${API_BASE_URL}/raw-materials/items?organization=${organization}`);
  },
  
  getItem: async (itemId) => {
    return await apiCall('GET', `${API_BASE_URL}/raw-materials/items/${itemId}`);
  },
  
  createItem: async (data) => {
    return await apiCall('POST', `${API_BASE_URL}/raw-materials/items`, { 
      body: JSON.stringify(data) 
    });
  },
  
  updateItem: async (itemId, data) => {
    return await apiCall('PUT', `${API_BASE_URL}/raw-materials/items/${itemId}`, { 
      body: JSON.stringify(data) 
    });
  },
  
  deleteItem: async (itemId) => {
    return await apiCall('DELETE', `${API_BASE_URL}/raw-materials/items/${itemId}`);
  },

  // Receiving
  getReceiving: async (receivingId) => {
    return await apiCall('GET', `${API_BASE_URL}/raw-materials/receive/${receivingId}`);
  },

  getReceivingUsage: async (receivingId) => {
    return await apiCall('GET', `${API_BASE_URL}/raw-materials/receive/${receivingId}/usage`);
  },

  createReceiving: async (data) => {
    return await apiCall('POST', `${API_BASE_URL}/raw-materials/receive`, { 
      body: JSON.stringify(data) 
    });
  },
  
  updateReceiving: async (receivingId, data) => {
    return await apiCall('PUT', `${API_BASE_URL}/raw-materials/receive/${receivingId}`, { 
      body: JSON.stringify(data) 
    });
  },
  
  completeReceiving: async (receivingId) => {
    return await apiCall('POST', `${API_BASE_URL}/raw-materials/receive/${receivingId}/complete`);
  },

  // QA Review
  getQAReviewItems: async (organization) => {
    return await apiCall('GET', `${API_BASE_URL}/raw-materials/qa-review?organization=${organization}`);
  },
  
  updateQAReview: async (receivingId, data) => {
    return await apiCall('PUT', `${API_BASE_URL}/raw-materials/qa-review/${receivingId}`, { 
      body: JSON.stringify(data) 
    });
  },
  
  completeQAReview: async (receivingId, data) => {
    return await apiCall('POST', `${API_BASE_URL}/raw-materials/qa-review/${receivingId}/complete`, { 
      body: JSON.stringify(data) 
    });
  },
  
  overrideCertificate: async (receivingId, reason) => {
    return await apiCall('POST', `${API_BASE_URL}/raw-materials/qa-review/${receivingId}/certificate-override`, { 
      body: JSON.stringify({ reason }) 
    });
  },
  
  // Packing Slip File Management
  uploadPackingSlips: async (receivingId, files) => {
    const formData = new FormData();
    files.forEach(file => {
      formData.append('files', file);
    });
    
    const token = localStorage.getItem('authToken');
    const response = await fetch(`${API_BASE_URL}/raw-materials/receive/${receivingId}/packing-slips/upload`, {
      method: 'POST',
      headers: {
        ...(token && { 'Authorization': `Bearer ${token}` }),
      },
      body: formData,
    });
    
    if (!response.ok) {
      const error = await response.json().catch(() => ({ error: 'Upload failed' }));
      throw new Error(error.error || error.message || 'Upload failed');
    }
    
    return await response.json();
  },
  
  deletePackingSlip: async (receivingId, fileUrl) => {
    return await apiCall('DELETE', `${API_BASE_URL}/raw-materials/receive/${receivingId}/packing-slips`, {
      body: JSON.stringify({ fileUrl })
    });
  },

  // Upload the quality certificate to Azure (persists a real, downloadable file).
  // Returns { certificateFileUrl (raw), certificateFileName, signedUrl }.
  uploadCertificate: async (receivingId, file) => {
    const formData = new FormData();
    formData.append('file', file);

    const token = localStorage.getItem('authToken');
    const response = await fetch(`${API_BASE_URL}/raw-materials/receive/${receivingId}/certificate/upload`, {
      method: 'POST',
      headers: {
        ...(token && { 'Authorization': `Bearer ${token}` }),
      },
      body: formData,
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ error: 'Upload failed' }));
      throw new Error(error.error || error.message || 'Certificate upload failed');
    }

    return await response.json();
  },
  
  // Inventory Adjustment/Reconciliation
  adjustInventory: async (receivingId, data) => {
    return await apiCall('POST', `${API_BASE_URL}/raw-materials/receive/${receivingId}/adjust`, { 
      body: JSON.stringify(data) 
    });
  },
  
  getAdjustmentHistory: async (receivingId) => {
    return await apiCall('GET', `${API_BASE_URL}/raw-materials/receive/${receivingId}/adjustments`);
  },
  
  getAvailableQuantity: async (receivingId) => {
    return await apiCall('GET', `${API_BASE_URL}/raw-materials/receive/${receivingId}/available`);
  },

  deleteReceiving: async (receivingId, reason, notes) => {
    return await apiCall('DELETE', `${API_BASE_URL}/raw-materials/receive/${receivingId}`, {
      body: JSON.stringify({ reason, notes }),
    });
  },

  updateWhoDidWhat: async (receivingId, updates) => {
    return await apiCall('PUT', `${API_BASE_URL}/raw-materials/receive/${receivingId}/who-did-what`, {
      body: JSON.stringify(updates),
    });
  },

  qualityEdit: async (receivingId, updates) => {
    return await apiCall('PUT', `${API_BASE_URL}/raw-materials/receive/${receivingId}/quality-edit`, {
      body: JSON.stringify(updates),
    });
  },

  // Lot Number Search for Recall Management
  searchLotNumber: async (searchTerm, organization) => {
    const params = new URLSearchParams({ search: searchTerm });
    if (organization) params.append('organization', organization);
    return await apiCall('GET', `${API_BASE_URL}/raw-materials/search/lot-number?${params}`);
  },

  // Inventory & Reports
  getApprovedMaterials: async (organization) => {
    return await apiCall('GET', `${API_BASE_URL}/raw-materials/approved?organization=${organization}`);
  },
  
  getLowStockItems: async (organization) => {
    return await apiCall('GET', `${API_BASE_URL}/raw-materials/low-stock?organization=${organization}`);
  },
  
  getStats: async (organization) => {
    return await apiCall('GET', `${API_BASE_URL}/raw-materials/stats?organization=${organization}`);
  },
};

// =====================================================
// RAW MATERIAL KITS API
// =====================================================
export const rawMaterialKitsAPI = {
  // Kits (Templates)
  getKits: async (organization) => {
    return await apiCall('GET', `${API_BASE_URL}/raw-material-kits?organization=${organization}`);
  },
  
  getKit: async (kitId) => {
    return await apiCall('GET', `${API_BASE_URL}/raw-material-kits/${kitId}`);
  },
  
  createKit: async (data) => {
    return await apiCall('POST', `${API_BASE_URL}/raw-material-kits`, { 
      body: JSON.stringify(data) 
    });
  },
  
  updateKit: async (kitId, data) => {
    return await apiCall('PUT', `${API_BASE_URL}/raw-material-kits/${kitId}`, { 
      body: JSON.stringify(data) 
    });
  },
  
  deleteKit: async (kitId) => {
    return await apiCall('DELETE', `${API_BASE_URL}/raw-material-kits/${kitId}`);
  },

  // Receiving Sessions
  createReceivingSession: async (kitId, data) => {
    return await apiCall('POST', `${API_BASE_URL}/raw-material-kits/${kitId}/receive`, { 
      body: JSON.stringify(data) 
    });
  },
  
  getReceivingSession: async (sessionId) => {
    return await apiCall('GET', `${API_BASE_URL}/raw-material-kits/receiving-sessions/${sessionId}`);
  },
  
  updateReceivingSession: async (sessionId, data) => {
    return await apiCall('PUT', `${API_BASE_URL}/raw-material-kits/receiving-sessions/${sessionId}`, { 
      body: JSON.stringify(data) 
    });
  },
  
  completeReceivingSession: async (sessionId) => {
    return await apiCall('POST', `${API_BASE_URL}/raw-material-kits/receiving-sessions/${sessionId}/complete`);
  },
  
  cancelReceivingSession: async (sessionId) => {
    return await apiCall('POST', `${API_BASE_URL}/raw-material-kits/receiving-sessions/${sessionId}/cancel`);
  },

  // Kit Inventory (Created kits from raw materials)
  getKitInventory: async (organization, options = {}) => {
    const params = new URLSearchParams({ organization });
    if (options.status) params.append('status', options.status);
    if (options.includeEmpty) params.append('includeEmpty', 'true');
    return await apiCall('GET', `${API_BASE_URL}/raw-material-kits/inventory?${params}`);
  },

  getKitInventoryById: async (inventoryId) => {
    return await apiCall('GET', `${API_BASE_URL}/raw-material-kits/inventory/${inventoryId}`);
  },

  createKitInventory: async (data) => {
    return await apiCall('POST', `${API_BASE_URL}/raw-material-kits/inventory`, {
      body: JSON.stringify(data)
    });
  },

  qaReviewKitInventory: async (inventoryId, data) => {
    return await apiCall('PUT', `${API_BASE_URL}/raw-material-kits/inventory/${inventoryId}/qa-review`, {
      body: JSON.stringify(data)
    });
  },

  updateKitInventoryItems: async (inventoryId, data) => {
    return await apiCall('PUT', `${API_BASE_URL}/raw-material-kits/inventory/${inventoryId}/update-items`, {
      body: JSON.stringify(data)
    });
  },

  useKitOnDonor: async (inventoryId, data) => {
    return await apiCall('POST', `${API_BASE_URL}/raw-material-kits/inventory/${inventoryId}/use-on-donor`, {
      body: JSON.stringify(data)
    });
  },

  getKitsForDonor: async (donorId) => {
    return await apiCall('GET', `${API_BASE_URL}/raw-material-kits/inventory/for-donor/${donorId}`);
  },
};

// =====================================================
// FORMULARY API
// =====================================================
export const formularyAPI = {
  // Templates
  getFormularies: async (organization) => {
    return await apiCall('GET', `${API_BASE_URL}/formularies?organization=${organization}`);
  },

  getFormulary: async (id) => {
    return await apiCall('GET', `${API_BASE_URL}/formularies/${id}`);
  },

  createFormulary: async (data) => {
    return await apiCall('POST', `${API_BASE_URL}/formularies`, {
      body: JSON.stringify(data)
    });
  },

  updateFormulary: async (id, data) => {
    return await apiCall('PUT', `${API_BASE_URL}/formularies/${id}`, {
      body: JSON.stringify(data)
    });
  },

  deleteFormulary: async (id) => {
    return await apiCall('DELETE', `${API_BASE_URL}/formularies/${id}`);
  },

  // Batches
  createBatch: async (templateId, data) => {
    return await apiCall('POST', `${API_BASE_URL}/formularies/${templateId}/batches`, {
      body: JSON.stringify(data)
    });
  },

  getBatch: async (batchId) => {
    return await apiCall('GET', `${API_BASE_URL}/formularies/batches/${batchId}`);
  },

  updateBatch: async (batchId, data) => {
    return await apiCall('PUT', `${API_BASE_URL}/formularies/batches/${batchId}`, {
      body: JSON.stringify(data)
    });
  },

  qaReviewBatch: async (batchId, data) => {
    return await apiCall('PUT', `${API_BASE_URL}/formularies/batches/${batchId}/qa-review`, {
      body: JSON.stringify(data)
    });
  },

  // Donor integration
  getAvailableForDonor: async (organization) => {
    return await apiCall('GET', `${API_BASE_URL}/formularies/available-for-donor?organization=${organization}`);
  },

  getDonorFormularies: async (donorId) => {
    return await apiCall('GET', `${API_BASE_URL}/donors/${donorId}/formularies`);
  },

  addFormularyToDonor: async (donorId, data) => {
    return await apiCall('POST', `${API_BASE_URL}/donors/${donorId}/formularies`, {
      body: JSON.stringify(data)
    });
  },

  removeFormularyFromDonor: async (donorId, usageId) => {
    return await apiCall('DELETE', `${API_BASE_URL}/donors/${donorId}/formularies/${usageId}`);
  },

  // Inventory adjustments
  adjustBatchOutput: async (batchId, data) => {
    return await apiCall('POST', `${API_BASE_URL}/formularies/batches/${batchId}/adjust`, {
      body: JSON.stringify(data)
    });
  },

  // Stats
  getStats: async (organization) => {
    return await apiCall('GET', `${API_BASE_URL}/formularies/stats?organization=${organization}`);
  },
};

// =====================================================
// CLIENT CONTACTS API - Direct Contact Management
// =====================================================
// Certificate of Processing (internal only) — read-only projection over the
// real donor lineage / QC release data, by manufactured-vial lot.
export const certificatesAPI = {
  listLots: async () => {
    return await apiCall('GET', `${API_BASE_URL}/certificates/lots`);
  },

  getByLot: async (lotNumber: string) => {
    return await apiCall('GET', `${API_BASE_URL}/certificates/by-lot/${encodeURIComponent(lotNumber)}`);
  },
};

// Marketing campaigns (internal only). Backed by /api/campaigns; friendly 503
// until the campaigns migration runs.
export const campaignsAPI = {
  list: async () => {
    return await apiCall('GET', `${API_BASE_URL}/campaigns`);
  },
  get: async (id: string) => {
    return await apiCall('GET', `${API_BASE_URL}/campaigns/${id}`);
  },
  create: async (data: any) => {
    return await apiCall('POST', `${API_BASE_URL}/campaigns`, { body: JSON.stringify(data) });
  },
  update: async (id: string, data: any) => {
    return await apiCall('PUT', `${API_BASE_URL}/campaigns/${id}`, { body: JSON.stringify(data) });
  },
  remove: async (id: string) => {
    return await apiCall('DELETE', `${API_BASE_URL}/campaigns/${id}`);
  },
  addMembers: async (id: string, leadIds: string[], channel?: string) => {
    return await apiCall('POST', `${API_BASE_URL}/campaigns/${id}/members`, { body: JSON.stringify({ leadIds, channel }) });
  },
  // Contact-level: contacts = [{ leadId, leadContactId }] (leadContactId null = whole lead).
  addMemberContacts: async (id: string, contacts: Array<{ leadId: string; leadContactId: string | null }>, channel?: string) => {
    return await apiCall('POST', `${API_BASE_URL}/campaigns/${id}/members`, { body: JSON.stringify({ contacts, channel }) });
  },
  removeMember: async (id: string, memberId: string) => {
    return await apiCall('DELETE', `${API_BASE_URL}/campaigns/${id}/members/${memberId}`);
  },
};

// Marketing workflows (automation: trigger + wait/send-email steps). Backed by
// /api/workflows; friendly 503 until the workflows migration runs.
export const workflowsAPI = {
  list: async () => {
    return await apiCall('GET', `${API_BASE_URL}/workflows`);
  },
  get: async (id: string) => {
    return await apiCall('GET', `${API_BASE_URL}/workflows/${id}`);
  },
  create: async (data: any) => {
    return await apiCall('POST', `${API_BASE_URL}/workflows`, { body: JSON.stringify(data) });
  },
  update: async (id: string, data: any) => {
    return await apiCall('PUT', `${API_BASE_URL}/workflows/${id}`, { body: JSON.stringify(data) });
  },
  remove: async (id: string) => {
    return await apiCall('DELETE', `${API_BASE_URL}/workflows/${id}`);
  },
  enroll: async (id: string, audienceType: string) => {
    return await apiCall('POST', `${API_BASE_URL}/workflows/${id}/enroll`, { body: JSON.stringify({ audienceType }) });
  },
  cancelEnrollment: async (id: string, enrollmentId: string) => {
    return await apiCall('POST', `${API_BASE_URL}/workflows/${id}/enrollments/${enrollmentId}/cancel`);
  },
};

// Social dashboard (internal only). Backed by /api/social; friendly 503 until
// the social migration runs. Live API sync is gated on each platform's creds.
export const socialAPI = {
  dashboard: async () => {
    return await apiCall('GET', `${API_BASE_URL}/social`);
  },
  connect: async (data: any) => {
    return await apiCall('POST', `${API_BASE_URL}/social/connect`, { body: JSON.stringify(data) });
  },
  update: async (id: string, data: any) => {
    return await apiCall('PUT', `${API_BASE_URL}/social/${id}`, { body: JSON.stringify(data) });
  },
  recordMetrics: async (id: string, data: any) => {
    return await apiCall('POST', `${API_BASE_URL}/social/${id}/metrics`, { body: JSON.stringify(data) });
  },
  disconnect: async (id: string) => {
    return await apiCall('POST', `${API_BASE_URL}/social/${id}/disconnect`);
  },
  remove: async (id: string) => {
    return await apiCall('DELETE', `${API_BASE_URL}/social/${id}`);
  },
};

// equicenta Field Use-Case Database (internal only). Backed by
// /api/field-cases; friendly 503 until the field_cases migration runs.
export const fieldCasesAPI = {
  list: async () => {
    return await apiCall('GET', `${API_BASE_URL}/field-cases`);
  },

  create: async (data: any) => {
    return await apiCall('POST', `${API_BASE_URL}/field-cases`, {
      body: JSON.stringify(data)
    });
  },

  update: async (caseId: string, data: any) => {
    return await apiCall('PUT', `${API_BASE_URL}/field-cases/${caseId}`, {
      body: JSON.stringify(data)
    });
  },

  remove: async (caseId: string) => {
    return await apiCall('DELETE', `${API_BASE_URL}/field-cases/${caseId}`);
  },
};

// Saved segments / marketing Lists (internal only). Backed by
// /api/saved-segments; friendly 503 until the saved_segments migration runs.
export const savedSegmentsAPI = {
  list: async () => {
    return await apiCall('GET', `${API_BASE_URL}/saved-segments`);
  },

  create: async (data: { name: string; definition: any }) => {
    return await apiCall('POST', `${API_BASE_URL}/saved-segments`, {
      body: JSON.stringify(data)
    });
  },

  remove: async (segmentId: string) => {
    return await apiCall('DELETE', `${API_BASE_URL}/saved-segments/${segmentId}`);
  },
};

// Native lead-capture forms (internal CRUD). Backed by /api/forms; returns a
// friendly 503 until the lead_forms migration has run.
export const formsAPI = {
  list: async () => apiCall('GET', `${API_BASE_URL}/forms`),
  get: async (id: string) => apiCall('GET', `${API_BASE_URL}/forms/${id}`),
  create: async (data: any) => apiCall('POST', `${API_BASE_URL}/forms`, { body: JSON.stringify(data) }),
  update: async (id: string, data: any) => apiCall('PUT', `${API_BASE_URL}/forms/${id}`, { body: JSON.stringify(data) }),
  remove: async (id: string) => apiCall('DELETE', `${API_BASE_URL}/forms/${id}`),
};

// Marketing emails: block-built broadcasts/templates with send + open/click
// tracking. Backed by /api/marketing-emails; 503 until the migration runs.
export const marketingEmailsAPI = {
  list: async (params: { kind?: string; status?: string } = {}) => {
    const q = new URLSearchParams();
    if (params.kind) q.set('kind', params.kind);
    if (params.status) q.set('status', params.status);
    const qs = q.toString();
    return apiCall('GET', `${API_BASE_URL}/marketing-emails${qs ? `?${qs}` : ''}`);
  },
  stats: async () => apiCall('GET', `${API_BASE_URL}/marketing-emails/stats`),
  audienceSize: async (audienceType: string) =>
    apiCall('GET', `${API_BASE_URL}/marketing-emails/audience-size?audienceType=${encodeURIComponent(audienceType || '')}`),
  get: async (id: string) => apiCall('GET', `${API_BASE_URL}/marketing-emails/${id}`),
  create: async (data: any) => apiCall('POST', `${API_BASE_URL}/marketing-emails`, { body: JSON.stringify(data) }),
  update: async (id: string, data: any) => apiCall('PUT', `${API_BASE_URL}/marketing-emails/${id}`, { body: JSON.stringify(data) }),
  remove: async (id: string) => apiCall('DELETE', `${API_BASE_URL}/marketing-emails/${id}`),
  sendTest: async (id: string, payload: string | { toAddress?: string; toAddresses?: string[] }) =>
    apiCall('POST', `${API_BASE_URL}/marketing-emails/${id}/test`, {
      body: JSON.stringify(typeof payload === 'string' ? { toAddress: payload } : payload),
    }),
  send: async (id: string, payload: { audienceType?: string; scheduledAt?: string } = {}) =>
    apiCall('POST', `${API_BASE_URL}/marketing-emails/${id}/send`, { body: JSON.stringify(payload) }),
  getTestRecipients: async () => apiCall('GET', `${API_BASE_URL}/marketing-emails/test-recipients`),
  getBanner: async () => apiCall('GET', `${API_BASE_URL}/marketing-emails/banner`),
  setBanner: async (data: { key: string | null; alt?: string }) =>
    apiCall('PUT', `${API_BASE_URL}/marketing-emails/banner`, { body: JSON.stringify(data) }),
};

// Marketing assets (images used in email/form builders). Backed by
// /api/marketing-assets; the public GET is served unauthenticated via a proxy
// route (marketing-assets/:key), never a SAS URL.
export const marketingAssetsAPI = {
  upload: async (file: File) => {
    const form = new FormData();
    form.append('file', file);
    const headers = { ...getAuthHeaders() } as Record<string, string>;
    delete headers['Content-Type'];
    const response = await fetch(`${API_BASE_URL}/marketing-assets`, {
      method: 'POST',
      headers,
      body: form,
    });
    return handleApiResponse(response);
  },
  publicUrl: (key: string): string => `${API_BASE_URL}/public/marketing-assets/${encodeURIComponent(key)}`,
};

// Public, no-auth form rendering + submission. Used by the public /f/:slug page.
export const publicFormsAPI = {
  getBySlug: async (slug: string) => {
    const url = `${API_BASE_URL}/public/forms/${encodeURIComponent(slug)}`;
    const response = await fetch(url, { method: 'GET', headers: { 'Content-Type': 'application/json' } });
    return await handleApiResponse(response);
  },
  submit: async (slug: string, values: Record<string, any>) => {
    const url = `${API_BASE_URL}/public/forms/${encodeURIComponent(slug)}/submit`;
    const response = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ values }),
    });
    return await handleApiResponse(response);
  },
  // Funnel tracking — fire-and-forget, errors ignored.
  view: (slug: string) => {
    fetch(`${API_BASE_URL}/public/forms/${encodeURIComponent(slug)}/view`, { method: 'POST', headers: { 'Content-Type': 'application/json' } }).catch(() => {});
  },
  start: (slug: string) => {
    fetch(`${API_BASE_URL}/public/forms/${encodeURIComponent(slug)}/start`, { method: 'POST', headers: { 'Content-Type': 'application/json' } }).catch(() => {});
  },
};

// Marketing-module tasks (internal only). Backed by /api/marketing-tasks;
// returns a friendly 503 until the marketing_tasks migration has run.
export const hubspotAPI = {
  /** Connection + per-scope health check. */
  getStatus: async () => {
    const url = `${API_BASE_URL}/marketing/hubspot/status`;
    const response = await fetch(url, { method: 'GET', headers: getAuthHeaders(), credentials: 'include' });
    return await handleApiResponse(response, { url, method: 'GET', headers: getAuthHeaders() });
  },
  /** List a HubSpot object: 'contacts' | 'companies' | 'deals' | 'leads' | 'forms'. */
  list: async (object, { limit = 50, after } = {}) => {
    const q = new URLSearchParams();
    if (limit) q.append('limit', String(limit));
    if (after) q.append('after', after);
    const url = `${API_BASE_URL}/marketing/hubspot/${object}?${q.toString()}`;
    const response = await fetch(url, { method: 'GET', headers: getAuthHeaders(), credentials: 'include' });
    return await handleApiResponse(response, { url, method: 'GET', headers: getAuthHeaders() });
  },
  /** Field metadata for an object: 'contacts' | 'companies' | 'deals' | 'leads'. */
  getProperties: async (object) => {
    const url = `${API_BASE_URL}/marketing/hubspot/properties/${object}`;
    const response = await fetch(url, { method: 'GET', headers: getAuthHeaders(), credentials: 'include' });
    return await handleApiResponse(response, { url, method: 'GET', headers: getAuthHeaders() });
  },
  /** Import HubSpot contacts into the local Leads table (unassigned). */
  importLeads: async (payload) => {
    const url = `${API_BASE_URL}/marketing/hubspot/import-leads`;
    const body = JSON.stringify(payload);
    const response = await fetch(url, { method: 'POST', headers: getAuthHeaders(), credentials: 'include', body });
    return await handleApiResponse(response, { url, method: 'POST', headers: getAuthHeaders(), body });
  },
  /**
   * Import HubSpot contacts AND assign the resulting leads to a rep in one action.
   * @param {{ contactIds: string[], repId: string | null }} payload
   */
  importAndAssign: async (payload) => {
    const url = `${API_BASE_URL}/marketing/hubspot/import-and-assign`;
    const body = JSON.stringify(payload);
    const response = await fetch(url, { method: 'POST', headers: getAuthHeaders(), credentials: 'include', body });
    return await handleApiResponse(response, { url, method: 'POST', headers: getAuthHeaders(), body });
  },
  /**
   * Mass-import every HubSpot contact not already in the system (deduped by email
   * vs clients + leads, skips no-email). Pass { dryRun: true } to preview counts
   * without writing.
   * @param {{ dryRun?: boolean, skipNoEmail?: boolean }} [payload]
   */
  importAll: async (payload = {}) => {
    const url = `${API_BASE_URL}/marketing/hubspot/import-all`;
    const body = JSON.stringify(payload);
    const response = await fetch(url, { method: 'POST', headers: getAuthHeaders(), credentials: 'include', body });
    return await handleApiResponse(response, { url, method: 'POST', headers: getAuthHeaders(), body });
  },
};

// Marketing config (territories). Internal only.
export const marketingAPI = {
  /** Saved state→rep assignments + per-rep caps + the reps to choose from. */
  getTerritories: async () => {
    const url = `${API_BASE_URL}/marketing/territories`;
    const response = await fetch(url, { method: 'GET', headers: getAuthHeaders(), credentials: 'include' });
    return await handleApiResponse(response, { url, method: 'GET', headers: getAuthHeaders() });
  },
  /** @param {{ assignments: Record<string,string>, caps: Record<string,number> }} payload */
  saveTerritories: async (payload) => {
    const url = `${API_BASE_URL}/marketing/territories`;
    const body = JSON.stringify(payload);
    const response = await fetch(url, { method: 'PUT', headers: getAuthHeaders(), credentials: 'include', body });
    return await handleApiResponse(response, { url, method: 'PUT', headers: getAuthHeaders(), body });
  },
  /** Last HubSpot sync result + whether auto-sync/auto-assign are enabled. */
  getSyncStatus: async () => {
    const url = `${API_BASE_URL}/marketing/sync`;
    const response = await fetch(url, { method: 'GET', headers: getAuthHeaders(), credentials: 'include' });
    return await handleApiResponse(response, { url, method: 'GET', headers: getAuthHeaders() });
  },
  /** Manual "Sync now" — runs the same intake the scheduled job does. */
  runSync: async () => {
    const url = `${API_BASE_URL}/marketing/sync/run`;
    const response = await fetch(url, { method: 'POST', headers: getAuthHeaders(), credentials: 'include', body: '{}' });
    return await handleApiResponse(response, { url, method: 'POST', headers: getAuthHeaders(), body: '{}' });
  },
  /**
   * Import a CSV/JSON lead list from any source into the pipeline.
   * @param {{ source: string, rows: Array<object> }} payload
   */
  importManual: async (payload) => {
    const url = `${API_BASE_URL}/marketing/import/manual`;
    const body = JSON.stringify(payload);
    const response = await fetch(url, { method: 'POST', headers: getAuthHeaders(), credentials: 'include', body });
    return await handleApiResponse(response, { url, method: 'POST', headers: getAuthHeaders(), body });
  },
  /** "Needs attention" counts (unassigned leads, pending validations, overdue tasks). */
  getAttention: async () => {
    const url = `${API_BASE_URL}/marketing/attention`;
    const response = await fetch(url, { method: 'GET', headers: getAuthHeaders(), credentials: 'include' });
    return await handleApiResponse(response, { url, method: 'GET', headers: getAuthHeaders() });
  },
};

export const marketingTasksAPI = {
  list: async (params: { status?: string; ownerId?: string } = {}) => {
    const query = new URLSearchParams();
    if (params.status) query.append('status', params.status);
    if (params.ownerId) query.append('ownerId', params.ownerId);
    return await apiCall('GET', `${API_BASE_URL}/marketing-tasks${query.toString() ? `?${query.toString()}` : ''}`);
  },

  create: async (data: any) => {
    return await apiCall('POST', `${API_BASE_URL}/marketing-tasks`, {
      body: JSON.stringify(data)
    });
  },

  update: async (taskId: string, data: any) => {
    return await apiCall('PUT', `${API_BASE_URL}/marketing-tasks/${taskId}`, {
      body: JSON.stringify(data)
    });
  },

  remove: async (taskId: string) => {
    return await apiCall('DELETE', `${API_BASE_URL}/marketing-tasks/${taskId}`);
  },
};

// Referrals (Flow 4). Internal only.
export const referralsAPI = {
  /** All referrals, or (with leadId) those where the lead is referrer or referred. */
  list: async (leadId?: string) => {
    const q = leadId ? `?leadId=${encodeURIComponent(leadId)}` : '';
    return await apiCall('GET', `${API_BASE_URL}/referrals${q}`);
  },
  create: async (data: any) => apiCall('POST', `${API_BASE_URL}/referrals`, { body: JSON.stringify(data) }),
  updateStatus: async (id: string, status: string) =>
    apiCall('PATCH', `${API_BASE_URL}/referrals/${id}/status`, { body: JSON.stringify({ status }) }),
};

export const clientContactsAPI = {
  /** All active practice contacts across the org's clients (internal only). */
  getAllContactsAdmin: async () => {
    return await apiCall('GET', `${API_BASE_URL}/client-contacts/admin/all`);
  },

  /**
   * Get all contacts for a client
   * Automatically uses perspective clientId if not provided
   */
  getContacts: async (clientUserId) => {
    // Use perspective client ID if not explicitly provided
    const targetClientId = clientUserId || getPerspectiveClientId();
    if (!targetClientId) {
      throw new Error('Client user ID is required');
    }
    return await apiCall('GET', `${API_BASE_URL}/client-contacts/${targetClientId}`);
  },

  // Add contact to a client
  addContact: async (contactData) => {
    return await apiCall('POST', `${API_BASE_URL}/client-contacts`, {
      body: JSON.stringify(contactData)
    });
  },

  // Update contact
  updateContact: async (contactId, data) => {
    return await apiCall('PUT', `${API_BASE_URL}/client-contacts/${contactId}`, {
      body: JSON.stringify(data)
    });
  },

  // Update portal access for contact
  updatePortalAccess: async (contactId, data) => {
    return await apiCall('PUT', `${API_BASE_URL}/client-contacts/${contactId}/portal-access`, {
      body: JSON.stringify(data)
    });
  },

  // Remove contact
  removeContact: async (contactId) => {
    return await apiCall('DELETE', `${API_BASE_URL}/client-contacts/${contactId}`);
  },

  // Search VET / VET_TECH contacts across all clients in an organization.
  // Used by the Vet Intro Pricing Exceptions admin UI.
  searchVets: async (query = '', organization = 'EPL', limit = 25) => {
    const params = new URLSearchParams();
    if (query) params.append('q', query);
    if (organization) params.append('organization', organization);
    if (limit) params.append('limit', String(limit));
    return await apiCall('GET', `${API_BASE_URL}/client-contacts/search/vets?${params.toString()}`);
  },
};

// =====================================================
// CLIENT SHIPPING ADDRESSES API - Multiple Shipping Addresses per Client
// =====================================================
export const clientShippingAddressesAPI = {
  /**
   * Get all shipping addresses for a user by user ID
   * Automatically uses perspective clientId if userId is not provided
   */
  getAddresses: async (userId) => {
    // Use perspective client ID if not explicitly provided
    const targetUserId = userId || getPerspectiveClientId();
    if (!targetUserId) {
      throw new Error('User ID is required');
    }
    return await apiCall('GET', `${API_BASE_URL}/client-shipping-addresses/user/${targetUserId}`);
  },

  // Get all shipping addresses by profile ID
  getAddressesByProfile: async (userProfileId) => {
    return await apiCall('GET', `${API_BASE_URL}/client-shipping-addresses/${userProfileId}`);
  },

  // Add a new shipping address
  addAddress: async (addressData) => {
    return await apiCall('POST', `${API_BASE_URL}/client-shipping-addresses`, {
      body: JSON.stringify(addressData)
    });
  },

  // Update an existing shipping address
  updateAddress: async (addressId, data) => {
    return await apiCall('PUT', `${API_BASE_URL}/client-shipping-addresses/${addressId}`, {
      body: JSON.stringify(data)
    });
  },

  // Set an address as default
  setDefault: async (addressId) => {
    return await apiCall('PUT', `${API_BASE_URL}/client-shipping-addresses/${addressId}/set-default`);
  },

  // Delete a shipping address
  deleteAddress: async (addressId) => {
    return await apiCall('DELETE', `${API_BASE_URL}/client-shipping-addresses/${addressId}`);
  },
};

// =====================================================
// PRACTICES API - Client Practice & Contact Management
// =====================================================
export const practicesAPI = {
  // Get all practices
  getPractices: async (params = {}) => {
    const queryParams = new URLSearchParams();
    if (params.organization) queryParams.append('organization', params.organization);
    if (params.status) queryParams.append('status', params.status);
    if (params.search) queryParams.append('search', params.search);
    if (params.page) queryParams.append('page', params.page);
    if (params.limit) queryParams.append('limit', params.limit);
    return await apiCall('GET', `${API_BASE_URL}/practices?${queryParams.toString()}`);
  },

  // Get single practice with contacts
  getPractice: async (practiceId) => {
    return await apiCall('GET', `${API_BASE_URL}/practices/${practiceId}`);
  },

  // Create practice with optional contacts
  createPractice: async (data) => {
    return await apiCall('POST', `${API_BASE_URL}/practices`, {
      body: JSON.stringify(data)
    });
  },

  // Update practice
  updatePractice: async (practiceId, data) => {
    return await apiCall('PUT', `${API_BASE_URL}/practices/${practiceId}`, {
      body: JSON.stringify(data)
    });
  },

  // Delete practice
  deletePractice: async (practiceId) => {
    return await apiCall('DELETE', `${API_BASE_URL}/practices/${practiceId}`);
  },

  // Add contact to practice
  addContact: async (practiceId, contactData) => {
    return await apiCall('POST', `${API_BASE_URL}/practices/${practiceId}/contacts`, {
      body: JSON.stringify(contactData)
    });
  },

  // Update contact
  updateContact: async (practiceId, contactId, data) => {
    return await apiCall('PUT', `${API_BASE_URL}/practices/${practiceId}/contacts/${contactId}`, {
      body: JSON.stringify(data)
    });
  },

  // Update portal access for contact
  updatePortalAccess: async (practiceId, contactId, data) => {
    return await apiCall('PUT', `${API_BASE_URL}/practices/${practiceId}/contacts/${contactId}/portal-access`, {
      body: JSON.stringify(data)
    });
  },

  // Remove contact
  removeContact: async (practiceId, contactId) => {
    return await apiCall('DELETE', `${API_BASE_URL}/practices/${practiceId}/contacts/${contactId}`);
  },

  // Get vets for a practice
  getVets: async (practiceId) => {
    return await apiCall('GET', `${API_BASE_URL}/practices/${practiceId}/vets`);
  },

  // Get practice by client user ID (helper)
  getPracticeByClientId: async (clientUserId) => {
    return await apiCall('GET', `${API_BASE_URL}/practices?clientUserId=${clientUserId}`);
  },
};

// =====================================================
// TRANSFER ORDERS API - Internal Inventory Transfers (TRAN-EPL-XXXX)
// =====================================================
export const transferOrdersAPI = {
  /**
   * Get all transfer orders (filtered by user type)
   * Automatically includes clientUserId when admin is viewing as client
   */
  getTransferOrders: async (params = {}) => {
    const queryParams = new URLSearchParams();
    if (params.page) queryParams.append('page', params.page);
    if (params.limit) queryParams.append('limit', params.limit);
    if (params.status) queryParams.append('status', params.status);
    if (params.clientUserId) queryParams.append('clientUserId', params.clientUserId);
    if (params.clinicId) queryParams.append('clinicId', params.clinicId);
    if (params.startDate) queryParams.append('startDate', params.startDate);
    if (params.endDate) queryParams.append('endDate', params.endDate);
    if (params.search) queryParams.append('search', params.search);
    
    // Add clientUserId when viewing as client (admin perspective) - if not already set
    if (!params.clientUserId) {
      const perspectiveClientId = getPerspectiveClientId();
      if (perspectiveClientId) {
        queryParams.append('clientUserId', perspectiveClientId);
      }
    }

    // Admin parent-company preview: scope transfers to every child of
    // the previewed parent_company. Backend ignores this for non-INTERNAL
    // callers; real PARENT_ADMIN aggregation happens server-side via
    // their own parentCompanyId regardless.
    if (!params.adminPreviewParentCompanyId) {
      const previewParentId = getAdminParentPreviewId();
      if (previewParentId) {
        queryParams.append('adminPreviewParentCompanyId', previewParentId);
      }
    } else {
      queryParams.append('adminPreviewParentCompanyId', params.adminPreviewParentCompanyId);
    }

    const queryString = queryParams.toString();
    return await apiCall('GET', `${API_BASE_URL}/transfer-orders${queryString ? `?${queryString}` : ''}`);
  },

  /**
   * Get single transfer order by ID
   */
  getTransferOrder: async (id) => {
    return await apiCall('GET', `${API_BASE_URL}/transfer-orders/${id}`);
  },

  /**
   * Create new transfer order (internal users only)
   */
  createTransferOrder: async (data) => {
    return await apiCall('POST', `${API_BASE_URL}/transfer-orders`, {
      body: JSON.stringify(data)
    });
  },

  /**
   * Update transfer order (only CREATED status, internal users only)
   */
  updateTransferOrder: async (id, data) => {
    return await apiCall('PUT', `${API_BASE_URL}/transfer-orders/${id}`, {
      body: JSON.stringify(data)
    });
  },

  /**
   * Start processing a transfer order
   */
  processTransferOrder: async (id) => {
    return await apiCall('POST', `${API_BASE_URL}/transfer-orders/${id}/process`);
  },

  /**
   * Mark transfer order as shipped
   */
  shipTransferOrder: async (id, data) => {
    return await apiCall('POST', `${API_BASE_URL}/transfer-orders/${id}/ship`, {
      body: JSON.stringify(data)
    });
  },

  /**
   * Mark transfer order as completed
   */
  completeTransferOrder: async (id, notes) => {
    return await apiCall('POST', `${API_BASE_URL}/transfer-orders/${id}/complete`, {
      body: JSON.stringify({ notes })
    });
  },

  /**
   * Mark transfer order as delivered (and complete it)
   */
  markAsDelivered: async (id, data?: { notes?: string; deliveredAt?: string }) => {
    return await apiCall('POST', `${API_BASE_URL}/transfer-orders/${id}/mark-delivered`, {
      body: JSON.stringify(data ?? {})
    });
  },

  /**
   * Get shipping rates for a transfer order
   */
  getRates: async (id, options = {}) => {
    const params = new URLSearchParams();
    if (options.packageWeightLbs && options.packageWeightLbs > 0) {
      params.append('packageWeightLbs', options.packageWeightLbs.toString());
    }
    if (options.dryIceWeightLbs && options.dryIceWeightLbs > 0) {
      params.append('dryIceWeightLbs', options.dryIceWeightLbs.toString());
    }
    if (options.packageSize) {
      params.append('packageSize', options.packageSize);
    }
    const queryString = params.toString();
    return await apiCall('GET', `${API_BASE_URL}/transfer-orders/${id}/rates${queryString ? `?${queryString}` : ''}`);
  },

  /**
   * Create shipping label for a transfer order
   */
  createLabel: async (id, carrierCode, serviceCode, shipDate, options = {}) => {
    const payload = {
      carrierCode,
      serviceCode,
      shipDate
    };
    if (options.weight) {
      payload.weight = options.weight;
    }
    if (options.dimensions) {
      payload.dimensions = options.dimensions;
    }
    if (options.dryIceWeightLbs && options.dryIceWeightLbs > 0) {
      payload.dryIceWeightLbs = options.dryIceWeightLbs;
    }
    return await apiCall('POST', `${API_BASE_URL}/transfer-orders/${id}/create-label`, {
      body: JSON.stringify(payload)
    });
  },

  /**
   * Fetch an existing ShipStation label for a transfer order (never creates a new one)
   */
  getLabel: async (id) => {
    return await apiCall('GET', `${API_BASE_URL}/transfer-orders/${id}/label`);
  },

  /**
   * Pick/scan an inventory item for a transfer order
   */
  pickItem: async (id, productId, inventoryId, lotNumber, quantity, skuId = null) => {
    return await apiCall('PUT', `${API_BASE_URL}/transfer-orders/${id}/pick-item`, {
      body: JSON.stringify({ productId, inventoryId, lotNumber, quantity, skuId })
    });
  },

  /**
   * Unpick/remove a previously picked inventory item
   */
  unpickItem: async (id, productId, inventoryId, quantity, skuId = null) => {
    return await apiCall('PUT', `${API_BASE_URL}/transfer-orders/${id}/unpick-item`, {
      body: JSON.stringify({ productId, inventoryId, quantity, skuId })
    });
  },

  /**
   * Get available inventory lots for a product
   */
  getAvailableLots: async (id, productId, source = 'epl', repId = null) => {
    const params = new URLSearchParams();
    if (source) params.append('source', source);
    if (repId) params.append('repId', repId);
    const queryString = params.toString();
    return await apiCall('GET', `${API_BASE_URL}/transfer-orders/${id}/available-lots/${productId}${queryString ? `?${queryString}` : ''}`);
  },

  /**
   * Cancel transfer order
   */
  cancelTransferOrder: async (id, reason) => {
    return await apiCall('POST', `${API_BASE_URL}/transfer-orders/${id}/cancel`, {
      body: JSON.stringify({ reason })
    });
  },

  /**
   * Delete transfer order (only CREATED status)
   */
  deleteTransferOrder: async (id) => {
    return await apiCall('DELETE', `${API_BASE_URL}/transfer-orders/${id}`);
  },

  /**
   * Get available inventory for transfer creation
   */
  getAvailableInventory: async (productId, source = 'epl', repId = null) => {
    const query = new URLSearchParams();
    if (productId) query.append('productId', productId);
    if (source) query.append('source', source);
    if (repId) query.append('repId', repId);
    const queryString = query.toString();
    return await apiCall('GET', `${API_BASE_URL}/transfer-orders/available-inventory${queryString ? `?${queryString}` : ''}`);
  },

  /**
   * Get client allocations
   * Automatically uses perspective clientId if not provided
   */
  getClientAllocations: async (clientUserId) => {
    // Use perspective client ID if not explicitly provided
    const targetClientId = clientUserId || getPerspectiveClientId();
    if (!targetClientId) {
      throw new Error('Client ID is required');
    }
    return await apiCall('GET', `${API_BASE_URL}/transfer-orders/client/${targetClientId}/allocations`);
  },

  /**
   * Get client allocations summary (aggregated by product)
   * Automatically uses perspective clientId if not provided
   */
  getClientAllocationsSummary: async (clientUserId) => {
    // Use perspective client ID if not explicitly provided
    const targetClientId = clientUserId || getPerspectiveClientId();
    if (!targetClientId) {
      throw new Error('Client ID is required');
    }
    return await apiCall('GET', `${API_BASE_URL}/transfer-orders/client/${targetClientId}/allocations-summary`);
  },

  // ============================================================================
  // REPORTING ENDPOINTS (Task 8.1 & 8.2)
  // ============================================================================

  /**
   * Get Main inventory summary with pending transfer commitments
   */
  getMainInventorySummary: async (organization = 'EPL') => {
    return await apiCall('GET', `${API_BASE_URL}/transfer-orders/reporting/main-inventory?organization=${organization}`);
  },

  /**
   * Get Main inventory aggregated by product
   */
  getMainInventoryByProduct: async (organization = 'EPL') => {
    return await apiCall('GET', `${API_BASE_URL}/transfer-orders/reporting/main-inventory-by-product?organization=${organization}`);
  },

  /**
   * Get pending transfer commitments
   */
  getPendingCommitments: async () => {
    return await apiCall('GET', `${API_BASE_URL}/transfer-orders/reporting/pending-commitments`);
  },

  /**
   * Get summary of all client allocations
   */
  getClientAllocationsSummaryAll: async () => {
    return await apiCall('GET', `${API_BASE_URL}/transfer-orders/reporting/client-allocations-summary`);
  },

  /**
   * Get allocation movement history
   */
  getAllocationMovementHistory: async (params = {}) => {
    const queryParams = new URLSearchParams();
    if (params.clientUserId) queryParams.append('clientUserId', params.clientUserId);
    if (params.transferOrderId) queryParams.append('transferOrderId', params.transferOrderId);
    if (params.limit) queryParams.append('limit', params.limit);
    
    const queryString = queryParams.toString();
    return await apiCall('GET', `${API_BASE_URL}/transfer-orders/reporting/allocation-movement-history${queryString ? `?${queryString}` : ''}`);
  },

  /**
   * Get transfer order system health
   */
  getSystemHealth: async () => {
    return await apiCall('GET', `${API_BASE_URL}/transfer-orders/system/health`);
  },

  /**
   * Get transfer order system statistics
   */
  getSystemStats: async () => {
    return await apiCall('GET', `${API_BASE_URL}/transfer-orders/system/stats`);
  },
};

/**
 * Perspective API - Admin View Switching
 * Allows admins to switch to client/rep views for troubleshooting
 */
export const perspectiveAPI = {
  /**
   * Switch to client view
   * @param {string} targetUserId - The client user ID to view as
   * @param {string} [reason] - Optional reason for the switch
   */
  switchToClientView: async (targetUserId, reason) => {
    return await apiCall('POST', `${API_BASE_URL}/admin/perspective/switch-to-client`, {
      body: JSON.stringify({ targetUserId, reason }),
    });
  },

  /**
   * Switch to rep view (future implementation)
   * @param {string} targetUserId - The rep user ID to view as
   * @param {string} [reason] - Optional reason for the switch
   */
  switchToRepView: async (targetUserId, reason) => {
    return await apiCall('POST', `${API_BASE_URL}/admin/perspective/switch-to-rep`, {
      body: JSON.stringify({ targetUserId, reason }),
    });
  },

  /**
   * Return to admin view from a switched perspective
   */
  returnToAdminView: async () => {
    return await apiCall('POST', `${API_BASE_URL}/admin/perspective/return-to-admin`);
  },

  /**
   * Get current perspective context
   */
  getCurrentPerspective: async () => {
    return await apiCall('GET', `${API_BASE_URL}/admin/perspective/current`);
  },

  /**
   * List perspective sessions for audit
   * @param {Object} options - Query options
   * @param {boolean} [options.activeOnly] - Only return active sessions
   * @param {number} [options.limit] - Maximum number of sessions to return
   */
  listSessions: async (options = {}) => {
    const params = new URLSearchParams();
    if (options.activeOnly) params.append('activeOnly', 'true');
    if (options.limit) params.append('limit', options.limit.toString());
    const query = params.toString();
    return await apiCall('GET', `${API_BASE_URL}/admin/perspective/sessions${query ? `?${query}` : ''}`);
  },

  /**
   * Record an action performed while in switched view
   * @param {string} action - Description of the action performed
   */
  recordAction: async (action) => {
    return await apiCall('POST', `${API_BASE_URL}/admin/perspective/record-action`, {
      body: JSON.stringify({ action }),
    });
  },
};

// =====================================================
// CONSIGNMENT INVOICING API - Invoice Management for Used Transfer Items
// =====================================================
export const consignmentInvoicingAPI = {
  /**
   * Get all pending billable items grouped by clinic
   * @param {Object} params - Query parameters
   * @param {string} [params.clinicId] - Filter by specific clinic
   */
  getBillableItems: async (params = {}) => {
    const queryParams = new URLSearchParams();
    if (params.clinicId) queryParams.append('clinicId', params.clinicId);
    const queryString = queryParams.toString();
    return await apiCall('GET', `${API_BASE_URL}/consignment-invoicing/billable-items${queryString ? `?${queryString}` : ''}`);
  },

  updateBillableItemPrice: async (trackingId, price) => {
    return await apiCall('PUT', `${API_BASE_URL}/consignment-invoicing/billable-items/${trackingId}/price`, {
      body: JSON.stringify({ price })
    });
  },

  /**
   * Create consignment invoice for selected items
   * @param {Object} data - Invoice data
   * @param {string[]} data.trackingIds - Array of ClientInventoryTracking IDs
   * @param {string} [data.invoiceType] - 'BATCH' or 'INDIVIDUAL'
   */
  createInvoice: async (data) => {
    return await apiCall('POST', `${API_BASE_URL}/consignment-invoicing/create-invoice`, {
      body: JSON.stringify(data)
    });
  },

  /**
   * Get invoiced history (past consignment invoices)
   * @param {Object} params - Query parameters
   * @param {number} [params.page] - Page number
   * @param {number} [params.limit] - Items per page
   * @param {string} [params.clinicId] - Filter by clinic
   * @param {string} [params.status] - Filter by payment status
   */
  getInvoicedHistory: async (params = {}) => {
    const queryParams = new URLSearchParams();
    if (params.page) queryParams.append('page', params.page.toString());
    if (params.limit) queryParams.append('limit', params.limit.toString());
    if (params.clinicId) queryParams.append('clinicId', params.clinicId);
    if (params.status) queryParams.append('status', params.status);
    const queryString = queryParams.toString();
    return await apiCall('GET', `${API_BASE_URL}/consignment-invoicing/invoiced-history${queryString ? `?${queryString}` : ''}`);
  },

  /**
   * Get summary stats for consignment invoicing dashboard
   */
  getSummary: async () => {
    return await apiCall('GET', `${API_BASE_URL}/consignment-invoicing/summary`);
  },
};

export const initializeAPI = (): void => {
  setupAPIInterceptors();

  // Setup global error handling
  window.addEventListener('unhandledrejection', (event) => {
    logger.error('Unhandled API Error:', event.reason);
    // Log to compliance system
    complianceAPI.logAuditEvent({
      eventType: 'API_ERROR',
      severity: 'ERROR',
      details: event.reason.message
    }).catch(() => {}); // Silent fail for logging
  });
};

// ============================================================================
// MD Review API (Public - no auth required)
// ============================================================================
export const mdReviewAPI = {
  getReview: async (token: string) => {
    const response = await fetch(`${API_BASE_URL}/md-review/${token}`);
    return response.json();
  },

  getDocumentUrl: (token: string, documentType: string): string => {
    return `${API_BASE_URL}/md-review/${token}/documents/${documentType}`;
  },

  submitReview: async (token: string, data: { status: string; signatureName: string; signatureTitle?: string; notes?: string }) => {
    const response = await fetch(`${API_BASE_URL}/md-review/${token}/submit`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    });
    return response.json();
  },

  // Authenticated - get MD reviews for a donor (for internal portal)
  getDonorMdReviews: async (donorId: string) => {
    return await apiCall('GET', `${API_BASE_URL}/donors/${donorId}/md-reviews`);
  },
};

/**
 * Audit Logs API (Internal only)
 */
export const auditLogsAPI = {
  /**
   * List audit logs with optional filters and pagination.
   */
  getLogs: async (params: {
    page?: number;
    pageSize?: number;
    action?: string;
    entityType?: string;
    entityId?: string;
    userId?: string;
    search?: string;
    dateFrom?: string;
    dateTo?: string;
  } = {}) => {
    const query = new URLSearchParams();
    Object.entries(params).forEach(([k, v]) => {
      if (v !== undefined && v !== '') query.append(k, String(v));
    });
    const response = await fetch(`${API_BASE_URL}/audit-logs?${query.toString()}`, {
      headers: getAuthHeaders(),
    });
    return handleApiResponse(response);
  },
};

// ===================================
// HORSES API
// ===================================

export const horsesAPI = {
  getClients: async () => {
    const response = await fetch(`${API_BASE_URL}/horses/clients`, {
      headers: getAuthHeaders(),
      credentials: 'include',
    });
    return handleApiResponse(response);
  },

  getReps: async () => {
    const response = await fetch(`${API_BASE_URL}/horses/reps`, {
      headers: getAuthHeaders(),
      credentials: 'include',
    });
    return handleApiResponse(response);
  },

  getHorses: async (params: {
    status?: string;
    search?: string;
    page?: number;
    limit?: number;
    clientId?: string;
    repId?: string;
  } = {}) => {
    const query = new URLSearchParams();
    // Include perspective client ID if in admin perspective mode
    const perspectiveClientId = getPerspectiveClientId();
    if (perspectiveClientId && !params.clientId) {
      query.append('clientId', perspectiveClientId);
    }
    Object.entries(params).forEach(([k, v]) => {
      if (v !== undefined && v !== '') query.append(k, String(v));
    });
    const response = await fetch(`${API_BASE_URL}/horses?${query.toString()}`, {
      headers: getAuthHeaders(),
      credentials: 'include',
    });
    return handleApiResponse(response);
  },

  getHorse: async (horseId: string) => {
    const response = await fetch(`${API_BASE_URL}/horses/${horseId}`, {
      headers: getAuthHeaders(),
      credentials: 'include',
    });
    return handleApiResponse(response);
  },

  createHorse: async (data: {
    name: string;
    breed?: string;
    sex?: string;
    color?: string;
    dateOfBirth?: string;
    weight?: number;
    notes?: string;
    clientUserId?: string;
  }) => {
    const perspectiveClientId = getPerspectiveClientId();
    const bodyData = perspectiveClientId && !data.clientUserId
      ? { ...data, clientUserId: perspectiveClientId }
      : data;
    const response = await fetch(`${API_BASE_URL}/horses`, {
      method: 'POST',
      headers: getAuthHeaders(),
      credentials: 'include',
      body: JSON.stringify(bodyData),
    });
    return handleApiResponse(response);
  },

  updateHorse: async (horseId: string, data: Record<string, unknown>) => {
    const response = await fetch(`${API_BASE_URL}/horses/${horseId}`, {
      method: 'PUT',
      headers: getAuthHeaders(),
      credentials: 'include',
      body: JSON.stringify(data),
    });
    return handleApiResponse(response);
  },

  deleteHorse: async (horseId: string) => {
    const response = await fetch(`${API_BASE_URL}/horses/${horseId}`, {
      method: 'DELETE',
      headers: getAuthHeaders(),
      credentials: 'include',
    });
    return handleApiResponse(response);
  },

  getDocuments: async (horseId: string) => {
    const response = await fetch(`${API_BASE_URL}/horses/${horseId}/documents`, {
      headers: getAuthHeaders(),
      credentials: 'include',
    });
    return handleApiResponse(response);
  },

  uploadDocument: async (horseId: string, file: File) => {
    const formData = new FormData();
    formData.append('file', file);
    const headers = getAuthHeaders();
    // Remove Content-Type so browser sets multipart boundary
    delete (headers as Record<string, string>)['Content-Type'];
    const response = await fetch(`${API_BASE_URL}/horses/${horseId}/documents`, {
      method: 'POST',
      headers,
      credentials: 'include',
      body: formData,
    });
    return handleApiResponse(response);
  },

  deleteDocument: async (horseId: string, docId: string) => {
    const response = await fetch(`${API_BASE_URL}/horses/${horseId}/documents/${docId}`, {
      method: 'DELETE',
      headers: getAuthHeaders(),
      credentials: 'include',
    });
    return handleApiResponse(response);
  },

  bulkMarkUsed: async (horseId: string, data: {
    items: Array<{ tissueId: string; injectionSite: string }>;
    dateUsed: string;
    notes?: string;
    usedForClinical?: boolean;
    isBillable?: boolean;
  }) => {
    const response = await fetch(`${API_BASE_URL}/horses/${horseId}/bulk-mark-used`, {
      method: 'POST',
      headers: getAuthHeaders(),
      credentials: 'include',
      body: JSON.stringify(data),
    });
    return handleApiResponse(response);
  },
};

// ===================================
// OPERATIONS API
// Every call here may 404 for users without an ops role; that is by design
// (the tab's existence is not advertised). Callers should treat any 404
// response as "no access" and render NoAccessNotFound.
// ===================================

export const operationsAPI = {
  /**
   * Returns the caller's current effective ops role. 404s if they have none.
   */
  getMyRole: async () => {
    const response = await fetch(`${API_BASE_URL}/operations/me/role`, {
      headers: getAuthHeaders(),
    });
    return handleApiResponse(response);
  },

  getMyUpcoming: async () => {
    const response = await fetch(`${API_BASE_URL}/operations/me/upcoming`, {
      headers: getAuthHeaders(),
    });
    return handleApiResponse(response);
  },

  getSchedule: async (params: {
    from?: string;
    to?: string;
    quarter?: string;
    priority?: string;
    status?: string;
    dependencyStatus?: string;
    departmentId?: string;
    personId?: string;
    includeDone?: boolean;
    includeNoDate?: boolean;
    includeOverdue?: boolean;
    blockedOnly?: boolean;
    unassignedOnly?: boolean;
  } = {}) => {
    const qs = new URLSearchParams();
    Object.entries(params).forEach(([k, v]) => {
      if (v !== undefined && v !== null && v !== '') qs.append(k, String(v));
    });
    const query = qs.toString();
    const response = await fetch(
      `${API_BASE_URL}/operations/schedule${query ? `?${query}` : ''}`,
      { headers: getAuthHeaders() },
    );
    return handleApiResponse(response);
  },

  /**
   * All portal users with an ops role. Available to anyone with ANY role.
   */
  listUsers: async () => {
    const response = await fetch(`${API_BASE_URL}/operations/users`, {
      headers: getAuthHeaders(),
    });
    return handleApiResponse(response);
  },

  /**
   * Active INTERNAL users who don't yet have an ops role. OPS_ADMIN only.
   */
  listGrantableUsers: async () => {
    const response = await fetch(`${API_BASE_URL}/operations/users/grantable`, {
      headers: getAuthHeaders(),
    });
    return handleApiResponse(response);
  },

  /**
   * Grant an ops role to a portal user. OPS_ADMIN only.
   */
  grantRole: async (data: { userId: string; role: string }) => {
    const response = await fetch(`${API_BASE_URL}/operations/roles`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify(data),
    });
    return handleApiResponse(response);
  },

  /**
   * Change a user's ops role. OPS_ADMIN only. Cannot target yourself.
   */
  changeRole: async (userId: string, role: string) => {
    const response = await fetch(`${API_BASE_URL}/operations/roles/${userId}`, {
      method: 'PATCH',
      headers: getAuthHeaders(),
      body: JSON.stringify({ role }),
    });
    return handleApiResponse(response);
  },

  /**
   * Revoke a user's ops role. OPS_ADMIN only. Cannot target yourself.
   */
  revokeRole: async (userId: string) => {
    const response = await fetch(`${API_BASE_URL}/operations/roles/${userId}`, {
      method: 'DELETE',
      headers: getAuthHeaders(),
    });
    return handleApiResponse(response);
  },

  // -----------------------------------------------------------------------
  // Departments (replace the old free-form "lead label" pattern)
  // -----------------------------------------------------------------------
  listDepartments: async () => {
    const response = await fetch(`${API_BASE_URL}/operations/departments`, {
      headers: getAuthHeaders(),
    });
    return handleApiResponse(response);
  },

  createDepartment: async (data: { name: string; color?: string | null; managerUserId?: string | null; sortOrder?: number }) => {
    const response = await fetch(`${API_BASE_URL}/operations/departments`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify(data),
    });
    return handleApiResponse(response);
  },

  updateDepartment: async (id: string, data: any) => {
    const response = await fetch(`${API_BASE_URL}/operations/departments/${id}`, {
      method: 'PATCH',
      headers: getAuthHeaders(),
      body: JSON.stringify(data),
    });
    return handleApiResponse(response);
  },

  deleteDepartment: async (id: string) => {
    const response = await fetch(`${API_BASE_URL}/operations/departments/${id}`, {
      method: 'DELETE',
      headers: getAuthHeaders(),
    });
    return handleApiResponse(response);
  },

  // -----------------------------------------------------------------------
  // Project Charter (tier=COMPANY_GOAL only)
  //
  // The Charter is edited as a single coherent document. PATCH accepts any
  // subset of fields + sub-lists; sub-lists use replace-list semantics so
  // sending objectives:[{...}] REPLACES the project's objective rows.
  // The autosave flow on the new-goal route sends the full current state
  // on a debounce.
  // -----------------------------------------------------------------------
  patchCharter: async (projectId: string, data: any) => {
    const response = await fetch(`${API_BASE_URL}/operations/projects/${projectId}/charter`, {
      method: 'PATCH',
      headers: getAuthHeaders(),
      body: JSON.stringify(data),
    });
    return handleApiResponse(response);
  },

  unlockCharter: async (projectId: string) => {
    const response = await fetch(`${API_BASE_URL}/operations/projects/${projectId}/charter/unlock`, {
      method: 'POST',
      headers: getAuthHeaders(),
    });
    return handleApiResponse(response);
  },

  signApprover: async (
    projectId: string,
    approverId: string,
    body: { password: string; decision: 'APPROVED' | 'REJECTED'; signatureMeaning: string }
  ) => {
    const response = await fetch(`${API_BASE_URL}/operations/projects/${projectId}/approvers/${approverId}/sign`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify(body),
    });
    return handleApiResponse(response);
  },

  // -----------------------------------------------------------------------
  // Projects
  // -----------------------------------------------------------------------
  listProjects: async (params: {
    quarter?: string;
    priority?: string;
    status?: string;
    dependencyStatus?: string;
    leadUserId?: string;
    personId?: string;
  } = {}) => {
    const qs = new URLSearchParams();
    Object.entries(params).forEach(([k, v]) => {
      if (v !== undefined && v !== '') qs.append(k, String(v));
    });
    const query = qs.toString();
    const response = await fetch(
      `${API_BASE_URL}/operations/projects${query ? `?${query}` : ''}`,
      { headers: getAuthHeaders() },
    );
    return handleApiResponse(response);
  },

  getProject: async (id: string) => {
    const response = await fetch(`${API_BASE_URL}/operations/projects/${id}`, {
      headers: getAuthHeaders(),
    });
    return handleApiResponse(response);
  },

  createProject: async (data: any) => {
    const response = await fetch(`${API_BASE_URL}/operations/projects`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify(data),
    });
    return handleApiResponse(response);
  },

  updateProject: async (id: string, data: any) => {
    const response = await fetch(`${API_BASE_URL}/operations/projects/${id}`, {
      method: 'PATCH',
      headers: getAuthHeaders(),
      body: JSON.stringify(data),
    });
    return handleApiResponse(response);
  },

  deleteProject: async (id: string) => {
    const response = await fetch(`${API_BASE_URL}/operations/projects/${id}`, {
      method: 'DELETE',
      headers: getAuthHeaders(),
    });
    return handleApiResponse(response);
  },

  rescheduleProject: async (id: string, deadline: string | null) => {
    const response = await fetch(`${API_BASE_URL}/operations/projects/${id}/reschedule`, {
      method: 'PATCH',
      headers: getAuthHeaders(),
      body: JSON.stringify({ deadline }),
    });
    return handleApiResponse(response);
  },

  // -----------------------------------------------------------------------
  // Subtasks
  // -----------------------------------------------------------------------
  createSubtask: async (projectId: string, data: any) => {
    const response = await fetch(`${API_BASE_URL}/operations/projects/${projectId}/subtasks`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify(data),
    });
    return handleApiResponse(response);
  },

  updateSubtask: async (id: string, data: any) => {
    const response = await fetch(`${API_BASE_URL}/operations/subtasks/${id}`, {
      method: 'PATCH',
      headers: getAuthHeaders(),
      body: JSON.stringify(data),
    });
    return handleApiResponse(response);
  },

  deleteSubtask: async (id: string) => {
    const response = await fetch(`${API_BASE_URL}/operations/subtasks/${id}`, {
      method: 'DELETE',
      headers: getAuthHeaders(),
    });
    return handleApiResponse(response);
  },

  undeleteSubtask: async (id: string) => {
    const response = await fetch(`${API_BASE_URL}/operations/subtasks/${id}/undelete`, {
      method: 'POST',
      headers: getAuthHeaders(),
    });
    return handleApiResponse(response);
  },

  rescheduleSubtask: async (id: string, deadline: string | null) => {
    const response = await fetch(`${API_BASE_URL}/operations/subtasks/${id}/reschedule`, {
      method: 'PATCH',
      headers: getAuthHeaders(),
      body: JSON.stringify({ deadline }),
    });
    return handleApiResponse(response);
  },

  // -----------------------------------------------------------------------
  // Comments
  // -----------------------------------------------------------------------
  listProjectComments: async (projectId: string) => {
    const response = await fetch(`${API_BASE_URL}/operations/projects/${projectId}/comments`, {
      headers: getAuthHeaders(),
    });
    return handleApiResponse(response);
  },

  listSubtaskComments: async (subtaskId: string) => {
    const response = await fetch(`${API_BASE_URL}/operations/subtasks/${subtaskId}/comments`, {
      headers: getAuthHeaders(),
    });
    return handleApiResponse(response);
  },

  /**
   * Unified activity feed for a project: comments + audit events
   * (project + its subtasks), merged and sorted newest-first.
   */
  getProjectActivity: async (projectId: string) => {
    const response = await fetch(`${API_BASE_URL}/operations/projects/${projectId}/activity`, {
      headers: getAuthHeaders(),
    });
    return handleApiResponse(response);
  },

  createComment: async (data: {
    projectId?: string;
    subtaskId?: string;
    bodyMarkdown: string;
    isProgressUpdate?: boolean;
    mentionedUserIds?: string[];
  }) => {
    const response = await fetch(`${API_BASE_URL}/operations/comments`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify(data),
    });
    return handleApiResponse(response);
  },

  // -----------------------------------------------------------------------
  // Attachments
  // -----------------------------------------------------------------------
  uploadAttachment: async (file: File, opts: { projectId?: string; subtaskId?: string }) => {
    if (!opts.projectId && !opts.subtaskId) {
      throw new Error('Must provide projectId or subtaskId');
    }
    const form = new FormData();
    form.append('file', file);
    if (opts.projectId) form.append('projectId', opts.projectId);
    if (opts.subtaskId) form.append('subtaskId', opts.subtaskId);
    // Don't set Content-Type -- let the browser add the multipart boundary.
    const headers = { ...getAuthHeaders() } as Record<string, string>;
    delete headers['Content-Type'];
    const response = await fetch(`${API_BASE_URL}/operations/attachments`, {
      method: 'POST',
      headers,
      body: form,
    });
    return handleApiResponse(response);
  },

  listProjectAttachments: async (projectId: string) => {
    const response = await fetch(`${API_BASE_URL}/operations/projects/${projectId}/attachments`, {
      headers: getAuthHeaders(),
    });
    return handleApiResponse(response);
  },

  deleteAttachment: async (id: string) => {
    const response = await fetch(`${API_BASE_URL}/operations/attachments/${id}`, {
      method: 'DELETE',
      headers: getAuthHeaders(),
    });
    return handleApiResponse(response);
  },
};

// =====================================================
// PARENT COMPANIES API - Multi-clinic billing entities
// =====================================================
//
// A parent company groups multiple child clients under a single billing
// entity (e.g. a vet who owns multiple clinics, or a holding company).
// Parent admin users (OWNER / BILLER) log into the portal and act on
// behalf of any child client. Child clients themselves do not log in.
//
// Email rule: parent admins are loginable -> email must be unique against
// other loginable users. Child clients are non-loginable -> their emails
// may collide with siblings or even with the same email under a different
// parent (the shared bookkeeping inbox case). The backend enforces this
// in utils/loginableEmail.ts; the UI only needs to call these endpoints.

export const parentCompaniesAPI = {
  /** List parent companies for an organization (defaults to caller's). */
  list: async (params: { organization?: string; includeDeleted?: boolean } = {}) => {
    const qs = new URLSearchParams();
    if (params.organization) qs.append('organization', params.organization);
    if (params.includeDeleted) qs.append('includeDeleted', 'true');
    const query = qs.toString();
    const response = await fetch(
      `${API_BASE_URL}/parent-companies${query ? `?${query}` : ''}`,
      { headers: getAuthHeaders() },
    );
    return handleApiResponse(response);
  },

  /** Detail view: parent company + admins (loginable users) + children. */
  get: async (id: string) => {
    const response = await fetch(`${API_BASE_URL}/parent-companies/${id}`, {
      headers: getAuthHeaders(),
    });
    return handleApiResponse(response);
  },

  create: async (data: { name: string; organization: string; sortOrder?: number }) => {
    const response = await fetch(`${API_BASE_URL}/parent-companies`, {
      method: 'POST',
      headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    });
    return handleApiResponse(response);
  },

  update: async (id: string, data: { name?: string; sortOrder?: number }) => {
    const response = await fetch(`${API_BASE_URL}/parent-companies/${id}`, {
      method: 'PATCH',
      headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    });
    return handleApiResponse(response);
  },

  /** Soft-delete: order history references the row, so we never hard-delete. */
  remove: async (id: string) => {
    const response = await fetch(`${API_BASE_URL}/parent-companies/${id}`, {
      method: 'DELETE',
      headers: getAuthHeaders(),
    });
    return handleApiResponse(response);
  },

  /** Attach an existing CLIENT user as a child of the parent company. */
  attachChild: async (id: string, userId: string) => {
    const response = await fetch(`${API_BASE_URL}/parent-companies/${id}/children`, {
      method: 'POST',
      headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
      body: JSON.stringify({ userId }),
    });
    return handleApiResponse(response);
  },

  /**
   * Create a brand-new child clinic and attach it to the parent in one
   * shot. Used when no unattached candidates exist to pick from.
   */
  createChildClient: async (
    id: string,
    data: {
      firstName: string;
      lastName: string;
      email: string;
      phone?: string | null;
      clinicName: string;
      licenseNumber?: string | null;
      address?: string | null;
      city?: string | null;
      state?: string | null;
      zipCode?: string | null;
    },
  ) => {
    const response = await fetch(
      `${API_BASE_URL}/parent-companies/${id}/children/create`,
      {
        method: 'POST',
        headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify(data),
      },
    );
    return handleApiResponse(response);
  },

  /** Detach a child client from the parent company (becomes standalone again). */
  detachChild: async (id: string, userId: string) => {
    const response = await fetch(
      `${API_BASE_URL}/parent-companies/${id}/children/${userId}`,
      { method: 'DELETE', headers: getAuthHeaders() },
    );
    return handleApiResponse(response);
  },

  /** Create a new parent admin user (OWNER or BILLER). */
  createAdmin: async (
    id: string,
    data: {
      email: string;
      firstName: string;
      lastName: string;
      password: string;
      parentRole: 'OWNER' | 'BILLER';
      phone?: string | null;
    },
  ) => {
    const response = await fetch(`${API_BASE_URL}/parent-companies/${id}/users`, {
      method: 'POST',
      headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    });
    return handleApiResponse(response);
  },

  /** Deactivate a parent admin (status -> INACTIVE; row preserved for history). */
  removeAdmin: async (id: string, userId: string) => {
    const response = await fetch(
      `${API_BASE_URL}/parent-companies/${id}/users/${userId}`,
      { method: 'DELETE', headers: getAuthHeaders() },
    );
    return handleApiResponse(response);
  },
};

// =====================================================
// PARENT ADMIN SELF-SERVICE API
// =====================================================
//
// Used by a logged-in PARENT_ADMIN to enumerate their child clinics and
// switch into one. The "switch" call drops them into the same
// AdminPerspectiveSession infrastructure that internal admins use, so
// the existing client-side data scoping (clientAPI auto-injecting
// clientId, ClientRouteWrapper, etc.) just works.

export const parentAPI = {
  /** Returns the parent admin's own row + their parent_company summary. */
  me: async () => {
    const response = await fetch(`${API_BASE_URL}/parent/me`, {
      headers: getAuthHeaders(),
    });
    return handleApiResponse(response);
  },

  /** Lists child clinics the caller can act on behalf of. */
  myChildren: async () => {
    const response = await fetch(`${API_BASE_URL}/parent/me/children`, {
      headers: getAuthHeaders(),
    });
    return handleApiResponse(response);
  },

  /** Switch into a child clinic. Body: { childUserId, reason? } */
  switchToChild: async (childUserId: string, reason?: string) => {
    const response = await fetch(`${API_BASE_URL}/parent/perspective/switch`, {
      method: 'POST',
      headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
      body: JSON.stringify({ childUserId, reason }),
    });
    return handleApiResponse(response);
  },

  /** Close any active perspective session and return to the picker. */
  returnToParent: async () => {
    const response = await fetch(`${API_BASE_URL}/parent/perspective/return`, {
      method: 'POST',
      headers: getAuthHeaders(),
    });
    return handleApiResponse(response);
  },

  /** Current perspective state (used to decide picker vs portal on reload). */
  currentPerspective: async () => {
    const response = await fetch(`${API_BASE_URL}/parent/perspective/current`, {
      headers: getAuthHeaders(),
    });
    return handleApiResponse(response);
  },
};

// =====================================================
// PARENT PORTAL API
// =====================================================
//
// Powers the unified parent admin portal where listings aggregate across
// every child clinic the caller's parent_company owns. Read-only --
// order placement still drops the parent admin into a specific child via
// the existing perspective infrastructure.

// Build a query string with the optional admin-preview param baked in.
// INTERNAL callers pass adminPreviewParentCompanyId to scope the response
// to a specific parent_company; PARENT_ADMIN callers ignore it (the
// backend uses their own parentCompanyId regardless).
const portalQuery = (
  params: Record<string, any> = {},
  previewParentCompanyId?: string,
) => {
  const qs = new URLSearchParams();
  Object.entries(params).forEach(([k, v]) => {
    if (v !== undefined && v !== null && v !== '') qs.append(k, String(v));
  });
  if (previewParentCompanyId) {
    qs.append('adminPreviewParentCompanyId', previewParentCompanyId);
  }
  return qs.toString() ? `?${qs.toString()}` : '';
};

export const parentPortalAPI = {
  /** Aggregated dashboard tiles + recent activity. */
  dashboardStats: async (previewParentCompanyId?: string) => {
    const url = `${API_BASE_URL}/parent/portal/dashboard-stats${portalQuery({}, previewParentCompanyId)}`;
    const response = await fetch(url, { headers: getAuthHeaders() });
    return handleApiResponse(response);
  },

  /** Aggregated orders list across all child clinics. */
  orders: async (
    params: {
      page?: number;
      limit?: number;
      status?: string;
      paymentStatus?: string;
      childUserId?: string;
      search?: string;
      startDate?: string;
      endDate?: string;
    } = {},
    previewParentCompanyId?: string,
  ) => {
    const url = `${API_BASE_URL}/parent/portal/orders${portalQuery(params, previewParentCompanyId)}`;
    const response = await fetch(url, { headers: getAuthHeaders() });
    return handleApiResponse(response);
  },

  /** Child clinics + per-clinic summary stats. */
  clinics: async (previewParentCompanyId?: string) => {
    const url = `${API_BASE_URL}/parent/portal/clinics${portalQuery({}, previewParentCompanyId)}`;
    const response = await fetch(url, { headers: getAuthHeaders() });
    return handleApiResponse(response);
  },
};
